import { z } from 'zod';
import { uuidSchema, pageCursorSchema, pageMetaSchema } from './common.js';
import { BASIS_CONSTANT_REGISTRY, type BasisConstantName, BULK_ENTRY_MAX_COUNT } from '../financials-constants.js';
import { USE_PHASE_PUE_MIN, USE_PHASE_PUE_MAX } from '../use-phase/use-phase-math.js';
import { projectedUsePhaseKgco2eqSchema } from './use-phase.js';
import { refreshCliffSchema } from './fmv-outlook.js';

/**
 * Wayfinder Financials — Zod DTOs (SIL-240, Wave 2 T5 of
 * resources/plans/wayfinder-financials-spec.md §6, §7).
 *
 * Backs the `apps/api/src/routes/client/financials*` route family (T7-T12)
 * and the `apps/customer` Financials screens (T13-T17). Enums are hand-mirrored
 * from `packages/shared/src/db.d.ts` (generated by `make types-gen` off
 * migrations 679-682) — do not redefine them independently; if a migration
 * changes the enum, update both here and confirm `db.d.ts` regenerated.
 *
 * Success responses are bare Zod-parsed JSON (no `{data:...}` envelope,
 * matching fmv-outlook.ts / clientDashboard.ts). Errors use the shared
 * `apiErrorSchema` (common.ts) `{error:{code,message}}` shape.
 */

// ---------------------------------------------------------------------------
// Enums — mirror packages/shared/src/db.d.ts (migrations 679-682)
// ---------------------------------------------------------------------------

/** db.d.ts `DepreciationMethod`. Phase 1 supports only straight_line +
 * declining_balance — `sum_of_years_digits` exists in the DB enum (future
 * tier) but is rejected at the DTO layer by `phase1DepreciationMethodSchema`
 * below (spec §2.2, §3.1). */
export const depreciationMethodSchema = z.enum([
  'declining_balance',
  'straight_line',
  'sum_of_years_digits',
]);
export type DepreciationMethod = z.infer<typeof depreciationMethodSchema>;

/** Phase-1 write gate: `sum_of_years_digits` is deferred (spec §2.2 "writes
 * reject it in phase 1"). Use this schema on POLICY WRITE bodies; use the
 * full `depreciationMethodSchema` on READ shapes so a future DB row using
 * the deferred value still parses instead of throwing on GET. */
export const phase1DepreciationMethodSchema = z.enum(['declining_balance', 'straight_line']);
export type Phase1DepreciationMethod = z.infer<typeof phase1DepreciationMethodSchema>;

/** db.d.ts `FinancialSource`. */
export const financialSourceSchema = z.enum(['csv', 'erp', 'manual']);
export type FinancialSource = z.infer<typeof financialSourceSchema>;

/** db.d.ts `FinancialMatch`. */
export const financialMatchSchema = z.enum(['asset_tag', 'manual', 'po', 'serial']);
export type FinancialMatch = z.infer<typeof financialMatchSchema>;

/** db.d.ts `FmvBand`. */
export const fmvBandSchema = z.enum(['high', 'low', 'med']);
export type FmvBand = z.infer<typeof fmvBandSchema>;

/** db.d.ts `ScenarioAction`. `buy` added SIL-571 (mig 729/730, spec §3.4/§6.3)
 * — a persisted dollar action, priced from the street-band median (see
 * `buySkuId`/`buyUnitPriceUsd`/`buyPriceSuppressed` below). `move` stays
 * carbon-only on Compare (request-scoped `carbonMoveCohortInputSchema`
 * below) — not promoted, unchanged from iteration 1. */
export const scenarioActionSchema = z.enum(['hold', 'itad', 'refresh', 'sell', 'buy']);
export type ScenarioAction = z.infer<typeof scenarioActionSchema>;

/** db.d.ts `ScenarioStatus` (mig 728, SIL-568). draft -> in_review -> approved.
 * NEVER accepted on the general scenario write/patch/replace schemas below —
 * only the three transition endpoints (`POST /:id/submit|approve|unapprove`)
 * may change it (spec §3.2). */
export const scenarioStatusSchema = z.enum(['draft', 'in_review', 'approved']);
export type ScenarioStatus = z.infer<typeof scenarioStatusSchema>;

/**
 * Policy resolution tier (spec §3.1, mig 682 `v_client_asset_book_value.policy_tier`).
 * Mirrors the CASE expression in 682_v_client_asset_book_value.sql exactly —
 * `class_override` (not the spec prose's `per_class`), `org_default`,
 * `platform_default`, `no_policy`.
 */
export const policyTierSchema = z.enum([
  'class_override',
  'org_default',
  'platform_default',
  'no_policy',
]);
export type PolicyTier = z.infer<typeof policyTierSchema>;

/**
 * Epic 3 (lots-first-class) — `policyTier` for a GROUP row (class, lot,
 * team, or site), a superset of `policyTierSchema` adding `'mixed'`: a
 * lot/team/site group's member assets resolved to more than one distinct
 * tier (each asset's book value is still correct, resolved per its OWN
 * class; this just means the group cannot report a single representative
 * tier the way a class group always can, by construction). Kept as its own
 * schema — rather than widening `policyTierSchema` itself — because
 * `resolvePolicy()`'s pure kernel (packages/shared) and `ResolvedPolicy`
 * NEVER produce `'mixed'`; widening the base enum would let that
 * impossible-in-practice value silently propagate into unrelated call sites
 * (capitalization policy tree, ERP policy-suggestion rows) that have no
 * group concept at all.
 */
export const groupPolicyTierSchema = z.enum([
  'class_override',
  'org_default',
  'platform_default',
  'no_policy',
  'mixed',
]);
export type GroupPolicyTier = z.infer<typeof groupPolicyTierSchema>;

/**
 * Epic 3 (lots-first-class plan) — the "Group by" dimension for the
 * point-in-time financial/FMV aggregate views (`/financials/summary`,
 * `/financials/depreciation`, `/financials/opportunity`). Closed enum, mapped
 * through a `const` record to literal Kysely column refs at every call site
 * — never interpolated into SQL (`sql.ref(request.dimension)` does not
 * validate against the schema and is a live injection sink).
 *
 * Time-series views (portfolio FMV daily chart) deliberately do NOT get this
 * parameter — lot/team membership has no history, so a per-group series would
 * chart today's membership against historical prices (plan "Epic 3 — scope").
 */
export const financialsGroupDimensionSchema = z.enum(['class', 'lot', 'team', 'site']);
export type FinancialsGroupDimension = z.infer<typeof financialsGroupDimensionSchema>;

/** Scenario horizon — DB CHECK restricts to these four values (mig 681). */
export const scenarioHorizonMonthsSchema = z.union([
  z.literal(12),
  z.literal(24),
  z.literal(36),
  z.literal(60),
]);
export type ScenarioHorizonMonths = z.infer<typeof scenarioHorizonMonthsSchema>;

/**
 * `financial_scenario.pue` / a transient `/compare` PUE override — bounded
 * server-side to `[1.0, 3.0]` (SIL-346, use-phase-carbon.md §PUE: "Bound
 * user-supplied PUE server-side ... and reject out-of-range rather than
 * clamp"). Zod's `.min()`/`.max()` REJECT (fail `.parse()`/400 the request)
 * rather than clamp, which is exactly the required behavior — this schema
 * must not be "fixed" to `.transform()`-clamp a value into range.
 * `financial_scenario.pue` is user-writable and multiplies straight into a
 * figure elsewhere labeled Scope 2, so an unbounded value here (e.g. 50)
 * would otherwise silently inflate/deflate that figure by 15-50x.
 */
export const scenarioPueSchema = z.coerce
  .number()
  .min(USE_PHASE_PUE_MIN)
  .max(USE_PHASE_PUE_MAX)
  .nullable();

// ---------------------------------------------------------------------------
// 0. CalculationBasis (SIL-291, W0) — Level-2 basis disclosure, spec
//    Cross-cutting "calculation transparency". Shared across every financials
//    endpoint that discloses how a figure was computed; NOT specific to one
//    report. See resources/plans/financials-hifi-flow-spec.md Cross-cutting.
// ---------------------------------------------------------------------------

/**
 * Closed source enum for a basis input — never free text (spec Cross-cutting
 * "never free text"). Extend this enum (never stringly-type a new source)
 * when a slice needs a source this list doesn't yet cover.
 */
export const basisSourceSchema = z.enum([
  'erp',
  'manual_entry',
  'sku_launch_price_estimate',
  'fmv_snapshot',
  'market_comps',
  'telemetry',
  'policy_class_override',
  'policy_org_default',
  'platform_default',
  /** SIL-351 — a wattage figure derived from `sku_oem_spec` nameplate power
   * fields (typical/max draw, PSU capacity, or gpu_count x tdp_watts),
   * duty-cycle (and, for the GPU-TDP rung, host-overhead) derated — never
   * measured telemetry. Distinct from `sku_launch_price_estimate` (a $ figure
   * estimate) and `telemetry` (measured, not derived). */
  'oem_nameplate_spec',
  /** SIL-436 — an owned cohort's carbon rate read verbatim off
   * `asset_use_phase_snapshot` (the scenario carbon kernel's 'owned_snapshot'
   * rate source, `scenario-carbon-kernel.ts`). Distinct from `telemetry`
   * (a wattage/dollar-side measured figure) — this is the device-level
   * kgCO2e/yr rate itself, not a power draw. */
  'asset_use_phase_snapshot',
  /** SIL-436 — a hypothetical-buy or post-move carbon rate derived from
   * `watts x PUE x grid_intensity_factor(target)` (the kernel's
   * 'buy_derived' rate source), citing the `grid_intensity_factor` (720)
   * row for the target country. */
  'grid_intensity_factor',
  /** SIL-436 — a carbon adder scalar (disposal WEEE factor, used-transport
   * km, used-refurb %) read from `customer_org_sustainability_config` (719)
   * because the compare request did not supply its own override. */
  'org_sustainability_config',
  /** SIL-436 — a buy/refresh-new-fleet cohort's per-unit embodied carbon,
   * read from `v_sku_carbon_footprint_latest`. */
  'sku_carbon_footprint',
]);
export type BasisSource = z.infer<typeof basisSourceSchema>;

/**
 * `{value, source, asOf, isFallback}` envelope a resolver
 * (`apps/api/src/lib`) returns for one basis input, BEFORE it is named and
 * merged into a `CalculationBasis` by `basisOf()`. `isFallback` is a
 * first-class boolean, never prose (spec Cross-cutting — candid fallback
 * rule, e.g. purchase-price -> SKU launch list price emits `isFallback: true`
 * + `source: sku_launch_price_estimate`).
 */
export interface BasisEnvelope {
  value: number | null;
  source: BasisSource;
  asOf: string;
  isFallback: boolean;
  /**
   * SIL-1828 (R2): present (true) only when the value derives, in whole or in
   * part, from a virtual (sum-of-components) baseline. Omitted otherwise so
   * existing basis payloads are unchanged. Server-derived; UIs never re-derive.
   */
  isDirectional?: boolean;
}

export const calculationBasisInputSchema = z.object({
  name: z.string().min(1),
  value: z.coerce.number().nullable(),
  source: basisSourceSchema,
  asOf: z.string(),
  isFallback: z.boolean(),
  isDirectional: z.boolean().optional(),
});
export type CalculationBasisInput = z.infer<typeof calculationBasisInputSchema>;

/**
 * The ONLY names a `CalculationBasis.constants[]` entry may use — generated
 * from `financials-constants.ts`'s `BASIS_CONSTANT_REGISTRY` keys, so the
 * allowlist is enforced by construction: a constant not registered there
 * cannot pass this schema (see `financials-dto.test.ts`'s serialization
 * test — a non-member key is asserted to fail `safeParse`).
 */
const BASIS_CONSTANT_NAMES = Object.keys(BASIS_CONSTANT_REGISTRY) as [
  BasisConstantName,
  ...BasisConstantName[],
];
export const basisConstantNameSchema = z.enum(BASIS_CONSTANT_NAMES);

export const calculationBasisConstantSchema = z.object({
  name: basisConstantNameSchema,
  value: z.coerce.number(),
  source: basisSourceSchema,
});
export type CalculationBasisConstant = z.infer<typeof calculationBasisConstantSchema>;

/**
 * Level-2 basis disclosure DTO (spec Cross-cutting). `formulaId` is a
 * constant defined beside the kernel it names (e.g.
 * `BOOK_VALUE_CLASS_ROW_FORMULA_ID` next to `bookValueAtAge` in
 * `book-value.ts`). Attaches per response section — one for totals, one per
 * class row only where inputs genuinely differ by class (policy tier,
 * comps) — never per-asset in a list payload (spec: "aggregate stays
 * aggregate").
 */
export const calculationBasisSchema = z.object({
  inputs: z.array(calculationBasisInputSchema),
  formulaId: z.string().min(1),
  constants: z.array(calculationBasisConstantSchema),
});
export type CalculationBasis = z.infer<typeof calculationBasisSchema>;

/**
 * Shared merge helper — spec Cross-cutting "the route merges via one shared
 * `basisOf()` helper". Resolvers return raw `BasisEnvelope`s; a pure
 * `describeX(inputs)` beside the kernel it documents names them into
 * `CalculationBasisInput[]`; this helper assembles the final
 * `CalculationBasis`, resolving each requested constant name against
 * `BASIS_CONSTANT_REGISTRY` (so a typo'd constant name is a TS compile error,
 * not a runtime surprise) and validating the whole shape through
 * `calculationBasisSchema.parse` before it ever reaches a response.
 */
export function basisOf(params: {
  formulaId: string;
  inputs: CalculationBasisInput[];
  constants?: BasisConstantName[];
}): CalculationBasis {
  return calculationBasisSchema.parse({
    formulaId: params.formulaId,
    inputs: params.inputs,
    constants: (params.constants ?? []).map((name) => ({
      name,
      value: BASIS_CONSTANT_REGISTRY[name].value,
      source: BASIS_CONSTANT_REGISTRY[name].source,
    })),
  });
}

// ---------------------------------------------------------------------------
// 1. Capitalization policy (T8) — org-default + per-class override
// ---------------------------------------------------------------------------

/** A stored `capitalization_policy` row (org-default when `assetClassId` is
 * null, an override otherwise). Mirrors db.d.ts `CapitalizationPolicy`. */
export const capitalizationPolicyRowSchema = z.object({
  id: uuidSchema,
  customerOrgId: uuidSchema,
  /** null = the org-wide default row; non-null = a per-class override. */
  assetClassId: z.string().nullable(),
  method: depreciationMethodSchema,
  usefulLifeYears: z.number().int().min(1).max(30),
  /** Required when method = declining_balance; ignored otherwise. */
  dbFactor: z.coerce.number().nullable(),
  salvagePct: z.coerce.number().min(0).max(100),
  capThresholdUsd: z.coerce.number().min(0),
  /** Only meaningful on the org-default row; per-class rows ignore it. */
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  createdBy: uuidSchema.nullable(),
  updatedBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CapitalizationPolicyRow = z.infer<typeof capitalizationPolicyRowSchema>;

/**
 * Shared refinement for policy write bodies (org-default PUT and per-class
 * override PUT, T8): phase-1 method gate (spec §2.2 — `sum_of_years_digits`
 * writes reject) + db_factor required for declining_balance (mirrors the DB
 * CHECK `capitalization_policy_db_factor_required`, defense-in-depth so the
 * API 400s before it ever reaches the DB constraint).
 */
function refinePolicyWrite<T extends { method: string; dbFactor?: number | null }>(
  schema: z.ZodType<T>,
) {
  return schema
    .refine((v) => v.method !== 'sum_of_years_digits', {
      message: 'sum_of_years_digits is deferred out of phase 1 — use straight_line or declining_balance',
      path: ['method'],
    })
    .refine((v) => v.method !== 'declining_balance' || v.dbFactor != null, {
      message: 'dbFactor is required when method = declining_balance',
      path: ['dbFactor'],
    });
}

const capitalizationPolicyWriteBaseSchema = z.object({
  method: depreciationMethodSchema,
  usefulLifeYears: z.number().int().min(1).max(30),
  dbFactor: z.coerce.number().positive().nullable().optional(),
  salvagePct: z.coerce.number().min(0).max(100).default(0),
  capThresholdUsd: z.coerce.number().min(0).default(0),
});

/** Body for `PUT /financials/policy/org-default` (T8, admin-only). */
export const orgDefaultPolicyWriteSchema = refinePolicyWrite(
  capitalizationPolicyWriteBaseSchema.extend({
    fiscalYearStartMonth: z.number().int().min(1).max(12).default(1),
  }),
);
export type OrgDefaultPolicyWrite = z.infer<typeof orgDefaultPolicyWriteSchema>;

/** Body for `PUT /financials/policy/class/:assetClassId` (T8, admin-only).
 * Per-class rows ignore `fiscalYearStartMonth`, so it is omitted here. */
export const classOverridePolicyWriteSchema = refinePolicyWrite(capitalizationPolicyWriteBaseSchema);
export type ClassOverridePolicyWrite = z.infer<typeof classOverridePolicyWriteSchema>;

/** The resolved policy for one asset class (or the org as a whole),
 * annotated with which tier answered (spec §3.1 resolvePolicy return shape —
 * locked here per §3.1's "lock this signature in T5/T6" note). `usefulLifeYears`
 * is numeric across all tiers (tier 3 is fractional in `book_depreciation_rate`).
 * `no_policy` carries every field null — never a silent zero. */
export const resolvedPolicySchema = z.object({
  tier: policyTierSchema,
  method: depreciationMethodSchema.nullable(),
  usefulLifeYears: z.coerce.number().nullable(),
  dbFactor: z.coerce.number().nullable(),
  salvagePct: z.coerce.number().nullable(),
  /** Derived annual rate, never stored — 1/usefulLife (straight_line) or
   * dbFactor/usefulLife (declining_balance), read-only in the UI. */
  annualRate: z.coerce.number().nullable(),
});
export type ResolvedPolicy = z.infer<typeof resolvedPolicySchema>;

/** One asset class's policy in the resolved tree (T8 `GET /financials/policy`). */
export const classPolicyTreeEntrySchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  resolved: resolvedPolicySchema,
  /** The raw override row when this class has one; null when inheriting. */
  override: capitalizationPolicyRowSchema.nullable(),
});
export type ClassPolicyTreeEntry = z.infer<typeof classPolicyTreeEntrySchema>;

/** Response for `GET /financials/policy` — whole resolved tree. */
export const capitalizationPolicyTreeResponseSchema = z.object({
  /** null when the org has never set an org-default row (empty state — every
   * class falls back further down the chain). */
  orgDefault: capitalizationPolicyRowSchema.nullable(),
  orgDefaultResolved: resolvedPolicySchema.nullable(),
  classes: z.array(classPolicyTreeEntrySchema),
});
export type CapitalizationPolicyTreeResponse = z.infer<typeof capitalizationPolicyTreeResponseSchema>;

// ---------------------------------------------------------------------------
// 2. Asset-financial entry + CSV import row (T9)
// ---------------------------------------------------------------------------

/** Phase 1 asserts USD-only (DB CHECK `purchase_currency = 'USD'`, spec §2.1 —
 * "stub for deferred multi-currency"). Mirrored here as a literal so a bad
 * write 400s before hitting the DB constraint. */
export const purchaseCurrencySchema = z.literal('USD');
export type PurchaseCurrency = z.infer<typeof purchaseCurrencySchema>;

/** A stored `asset_financial` row. Mirrors db.d.ts `AssetFinancial`. */
export const assetFinancialRowSchema = z.object({
  assetId: uuidSchema,
  customerOrgId: uuidSchema,
  /** null -> book value falls back to the SKU launch list price with an
   * estimate flag (§3.2, `v_client_asset_book_value.is_estimate`). */
  purchasePriceUsd: z.coerce.number().nullable(),
  purchasedAt: z.string().nullable(),
  /** Depreciation clock (SIL-229); null falls back to purchasedAt at read time. */
  inServiceAt: z.string().nullable(),
  poRef: z.string().nullable(),
  invoiceRef: z.string().nullable(),
  purchaseCurrency: purchaseCurrencySchema,
  source: financialSourceSchema,
  sourceSystem: z.string().nullable(),
  sourceRecordId: z.string().nullable(),
  matchedBy: financialMatchSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AssetFinancialRow = z.infer<typeof assetFinancialRowSchema>;

/** Body for `POST /asset-financial` (admin-only, T9). */
export const assetFinancialWriteSchema = z.object({
  assetId: uuidSchema,
  purchasePriceUsd: z.coerce.number().nonnegative().nullable().optional(),
  purchasedAt: z.string().nullable().optional(),
  inServiceAt: z.string().nullable().optional(),
  poRef: z.string().nullable().optional(),
  invoiceRef: z.string().nullable().optional(),
  purchaseCurrency: purchaseCurrencySchema.default('USD'),
});
export type AssetFinancialWrite = z.infer<typeof assetFinancialWriteSchema>;

/** Body for `PATCH /asset-financial/:assetId` (admin-only, partial edit, T9). */
export const assetFinancialPatchSchema = assetFinancialWriteSchema
  .omit({ assetId: true, purchaseCurrency: true })
  .partial();
export type AssetFinancialPatch = z.infer<typeof assetFinancialPatchSchema>;

/** One row of the serial-matched CSV import (T9, reuses the `import.ts`
 * staging pattern). `rowNum` is 1-based, matching `import_batch` conventions. */
export const assetFinancialImportRowSchema = z.object({
  rowNum: z.number().int().positive(),
  serialNumber: z.string().min(1),
  purchasePriceUsd: z.coerce.number().nonnegative().nullable().optional(),
  purchasedAt: z.string().nullable().optional(),
  inServiceAt: z.string().nullable().optional(),
  poRef: z.string().nullable().optional(),
  invoiceRef: z.string().nullable().optional(),
});
export type AssetFinancialImportRow = z.infer<typeof assetFinancialImportRowSchema>;

/** Per-row outcome in the import response — `errors` for a rejected row,
 * `warning` for the "would overwrite manual entry" re-import guard (spec §6 —
 * a csv row never silently clobbers a manual-sourced row unless forced). */
export const assetFinancialImportRowResultSchema = z.object({
  rowNum: z.number().int().positive(),
  errors: z.array(z.string()).optional(),
  warning: z.string().optional(),
});
export type AssetFinancialImportRowResult = z.infer<typeof assetFinancialImportRowResultSchema>;

/** Response for `POST /asset-financial/import` (sync, ≤10k rows, spec §6). */
export const assetFinancialImportResponseSchema = z.object({
  batchId: uuidSchema,
  rowCountTotal: z.number().int().nonnegative(),
  rowCountAccepted: z.number().int().nonnegative(),
  rowCountSkipped: z.number().int().nonnegative(),
  rowErrors: z.array(assetFinancialImportRowResultSchema),
});
export type AssetFinancialImportResponse = z.infer<typeof assetFinancialImportResponseSchema>;

/**
 * Body for `POST /asset-financial/bulk` (admin-only, SIL-298, W3, spec slice
 * 3) — one form applied per-unit to every selected asset. `assetIds` is
 * capped at `BULK_ENTRY_MAX_COUNT` (financials-hifi-flow-spec.md
 * Tenant-isolation invariants: "request size caps — bulk entry N <= 500").
 * `overwrite` only ever touches an existing `source='manual'` row (enforced
 * at the SQL level in the route, not just here) — ERP/CSV rows are never
 * touchable from this path.
 */
export const assetFinancialBulkWriteSchema = z.object({
  assetIds: z.array(uuidSchema).min(1).max(BULK_ENTRY_MAX_COUNT),
  purchasePriceUsd: z.coerce.number().nonnegative(),
  purchasedAt: z.string(),
  poRef: z.string().nullable().optional(),
  overwrite: z.boolean().default(false),
});
export type AssetFinancialBulkWrite = z.infer<typeof assetFinancialBulkWriteSchema>;

/**
 * Response for `POST /asset-financial/bulk` — reuses
 * `assetFinancialImportRowResultSchema`'s per-row result shape (spec: "no
 * new partial-failure contract"). `rowNum` is the 1-based index into the
 * de-duplicated `assetIds` request array (there is no CSV/staging row here).
 */
export const assetFinancialBulkResponseSchema = z.object({
  assetCountTotal: z.number().int().nonnegative(),
  assetCountAccepted: z.number().int().nonnegative(),
  assetCountSkipped: z.number().int().nonnegative(),
  rowErrors: z.array(assetFinancialImportRowResultSchema),
});
export type AssetFinancialBulkResponse = z.infer<typeof assetFinancialBulkResponseSchema>;

// ---------------------------------------------------------------------------
// 3. Scenario + cohort (T10, T10b) — selector jsonb per spec §2.3/§6
// ---------------------------------------------------------------------------

/**
 * Cohort membership predicate (spec §2.3 `financial_scenario_cohort.selector`,
 * jsonb). No DB-level CHECK validates array contents (IDOR guard C2 is
 * enforced by dereferencing under the caller's RLS scope at write/compute
 * time, not by schema) — this Zod shape only validates the shape, not
 * ownership. Portfolio default = `{all:true}` minus the other cohorts.
 */
export const scenarioCohortSelectorSchema = z.object({
  assetClassId: z.string().optional(),
  assetIds: z.array(uuidSchema).optional(),
  lotId: uuidSchema.optional(),
  teamId: uuidSchema.optional(),
  all: z.boolean().optional(),
});
export type ScenarioCohortSelector = z.infer<typeof scenarioCohortSelectorSchema>;

/** A stored `financial_scenario_cohort` row. Mirrors db.d.ts `FinancialScenarioCohort`. */
export const financialScenarioCohortRowSchema = z.object({
  id: uuidSchema,
  scenarioId: uuidSchema,
  customerOrgId: uuidSchema,
  label: z.string().min(1),
  selector: scenarioCohortSelectorSchema,
  action: scenarioActionSchema,
  timingMonths: z.number().int().min(0),
  /** Required when action = refresh (DB CHECK `..._refresh_sku_required`). */
  refreshSkuId: uuidSchema.nullable(),
  /** Old:new ratio for `refresh`; REUSED (SIL-571, mig 730's header comment)
   * for `buy` as the purchase's unit count — the two never collide on one
   * row since `action` is a single enum value. */
  consolidationRatio: z.coerce.number().positive().nullable(),
  /** SIL-571 (mig 730). Required when action = buy (DB CHECK
   * `financial_scenario_cohort_buy_sku_required`). Parallel to
   * `refreshSkuId`, never a shared column (spec §6.3). */
  buySkuId: uuidSchema.nullable(),
  /** SIL-571 — server-computed street-band median at cohort-save time,
   * NEVER client-supplied (see `financialScenarioCohortWriteBaseSchema`'s
   * docstring below — this field is absent from every write body). NULL
   * when `buyPriceSuppressed` is true. */
  buyUnitPriceUsd: z.coerce.number().nullable(),
  /** SIL-571 — true when the buy SKU's market depth is below
   * `COMPS_K_ANONYMITY_FLOOR` (or has no pricing snapshot at all). A
   * suppressed buy cohort is excluded from the scenario's economics and
   * blocks approval, but never blocks save. Server-computed, same posture
   * as `buyUnitPriceUsd`. */
  buyPriceSuppressed: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FinancialScenarioCohortRow = z.infer<typeof financialScenarioCohortRowSchema>;

/**
 * SIL-571 — `buySkuId` is the ONLY buy-related field a write body may set.
 * `buyUnitPriceUsd`/`buyPriceSuppressed` are deliberately ABSENT here (and
 * therefore stripped by Zod's default strip behavior if a caller sends
 * them) — they are always server-computed from `sku_pricing_snapshot` at
 * write time (POST /scenarios, POST/PATCH/PUT .../cohorts), mirroring the
 * approve-handler TOCTOU posture (spec §3.2/§3.4): a client cannot buy its
 * way to a cheaper cohort by supplying its own price.
 */
const financialScenarioCohortWriteBaseSchema = z.object({
  label: z.string().min(1),
  selector: scenarioCohortSelectorSchema,
  action: scenarioActionSchema,
  timingMonths: z.number().int().min(0).default(0),
  refreshSkuId: uuidSchema.nullable().optional(),
  /** null defaults server-side to fp16-derived (GPUs) or 1:1 (spec §3.4).
   * For `action = 'buy'`, this is the purchase's unit count instead (see
   * `financialScenarioCohortRowSchema.consolidationRatio`'s docstring) —
   * null defaults to 1 unit server-side. */
  consolidationRatio: z.coerce.number().positive().nullable().optional(),
  /** Required when action = buy (mirrors the DB CHECK). */
  buySkuId: uuidSchema.nullable().optional(),
});

/** Body for a cohort in `POST /scenarios` / `PUT .../cohorts/:id` (mirrors the
 * DB CHECKs `action <> 'refresh' OR refresh_sku_id IS NOT NULL` and
 * `action <> 'buy' OR buy_sku_id IS NOT NULL`, spec §2.3/§6.3). */
export const financialScenarioCohortWriteSchema = financialScenarioCohortWriteBaseSchema
  .refine((v) => v.action !== 'refresh' || v.refreshSkuId != null, {
    message: 'refreshSkuId is required when action = refresh',
    path: ['refreshSkuId'],
  })
  .refine((v) => v.action !== 'buy' || v.buySkuId != null, {
    message: 'buySkuId is required when action = buy',
    path: ['buySkuId'],
  });
export type FinancialScenarioCohortWrite = z.infer<typeof financialScenarioCohortWriteSchema>;

/** Body for `PATCH /scenarios/:id/cohorts/:cohortId` (T10, partial edit). */
export const financialScenarioCohortPatchSchema = financialScenarioCohortWriteBaseSchema.partial();
export type FinancialScenarioCohortPatch = z.infer<typeof financialScenarioCohortPatchSchema>;

/** SIL-532 (I2-C2, `wayfinder-iter2-spec.md` §3.3) — one dated assumption
 * row, as resolved by `apps/api/src/lib/scenario-staleness.ts` and compared
 * by `evaluateScenarioStaleness` (`packages/shared/src/scenario-staleness.ts`).
 * `id` matches a `SensitivityAttributeId` (`fmv_band` | `refresh_street_price`
 * | `pue`) so the assumption-table UI joins its own rows onto this array by
 * `id` for per-row stale badges. */
export const datedAssumptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  pricedAt: z.string(),
  stale: z.boolean(),
});
export type DatedAssumptionDto = z.infer<typeof datedAssumptionSchema>;

/** SIL-532 (I2-C2) — the ONE staleness read model shape embedded on both
 * `GET /scenarios` (list badges + Overview counter) and `GET /scenarios/:id`
 * (assumption-table row badges). `comparedAgainst` is the scenario's
 * `updatedAt` — `financial_scenario` has no `computed_at` column (checked
 * against both 681 and 728); see `evaluateScenarioStaleness`'s docstring for
 * the full rationale. */
export const scenarioStalenessSchema = z.object({
  scenarioId: uuidSchema,
  stale: z.boolean(),
  comparedAgainst: z.string(),
  assumptions: z.array(datedAssumptionSchema),
});
export type ScenarioStalenessDto = z.infer<typeof scenarioStalenessSchema>;

/** A stored `financial_scenario` row, optionally embedding its cohorts on GET.
 * Mirrors db.d.ts `FinancialScenario`. */
export const financialScenarioRowSchema = z.object({
  id: uuidSchema,
  customerOrgId: uuidSchema,
  name: z.string().min(1),
  horizonMonths: scenarioHorizonMonthsSchema,
  fmvBand: fmvBandSchema,
  kwhUsd: z.coerce.number().nonnegative(),
  // Deliberately NOT `scenarioPueSchema` — this is a READ of an already-
  // stored row, not a write boundary. Rejecting on read would turn any
  // legacy out-of-range value (pre-dating SIL-346's write-side bound) into
  // a 500 on every future GET of that scenario instead of just refusing the
  // write that produced it. Write paths (`financialScenarioWriteSchema`,
  // `scenarioCompareRequestSchema`) use `scenarioPueSchema` and reject.
  pue: z.coerce.number().positive().nullable(),
  itadRecoveryPct: z.coerce.number().min(0).max(100),
  createdBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** SIL-568 approval workflow (mig 728, spec §3.2/§6.1). status is
   * read-only here — never accepted on write/patch/replace bodies below;
   * only the three transition endpoints may change it. */
  status: scenarioStatusSchema,
  approvedBy: uuidSchema.nullable(),
  approvedAt: z.string().nullable(),
  /** Server-recomputed refreshCapexUsd at approval time (never a
   * client-supplied or stale-compare figure). Cleared on unapprove. */
  approvedCapexUsd: z.coerce.number().nullable(),
  /** scenario_approval_setting.capexThresholdUsd as read in the same
   * transaction as the approval decision. Cleared on unapprove. */
  approvedThresholdUsd: z.coerce.number().nullable(),
  updatedBy: uuidSchema.nullable(),
  /** SIL team-ownership (mig 776) — owning team. NULL = unassigned
   * (admin-only to modify, mirrors asset.team_id's NULL posture). Read-only
   * here — reassigned only via PUT /api/client/teams/scenario/:id
   * (client_assign_scenario_team, admin-gated). */
  teamId: uuidSchema.nullable(),
  /** SIL team-ownership — the owning team's display name, joined in for the
   * list/detail badges. Null when teamId is null OR (defensively) when the
   * team row can't be resolved. */
  teamName: z.string().nullable(),
  cohorts: z.array(financialScenarioCohortRowSchema).optional(),
  /** SIL-532 (I2-C2) — present on both GET /scenarios and GET /scenarios/:id
   * (see scenarioStalenessSchema's docstring). Optional here only so a
   * historical/mocked fixture that predates this field still parses. */
  staleness: scenarioStalenessSchema.optional(),
});
export type FinancialScenarioRow = z.infer<typeof financialScenarioRowSchema>;

/** Body for `POST /scenarios` (any member, T10). */
export const financialScenarioWriteSchema = z.object({
  name: z.string().min(1),
  horizonMonths: scenarioHorizonMonthsSchema.default(36),
  fmvBand: fmvBandSchema.default('med'),
  kwhUsd: z.coerce.number().nonnegative().default(0.12),
  pue: scenarioPueSchema.optional(),
  itadRecoveryPct: z.coerce.number().min(0).max(100).default(34),
  cohorts: z.array(financialScenarioCohortWriteSchema).optional(),
});
export type FinancialScenarioWrite = z.infer<typeof financialScenarioWriteSchema>;

/** Body for `PATCH /scenarios/:id` (T10, partial edit — top-level fields only;
 * cohorts are edited via the dedicated cohort sub-routes). */
export const financialScenarioPatchSchema = financialScenarioWriteSchema
  .omit({ cohorts: true })
  .partial();
export type FinancialScenarioPatch = z.infer<typeof financialScenarioPatchSchema>;

/**
 * A cohort inside a `PUT /scenarios/:id` body. Identical to the POST cohort
 * shape plus an optional `id`: present means "update this existing cohort",
 * absent means "insert a new one". Any stored cohort whose id is NOT in the
 * submitted array is deleted.
 */
export const financialScenarioCohortReplaceSchema = financialScenarioCohortWriteBaseSchema
  .extend({ id: uuidSchema.optional() })
  .refine((v) => v.action !== 'refresh' || v.refreshSkuId != null, {
    message: 'refreshSkuId is required when action = refresh',
    path: ['refreshSkuId'],
  })
  .refine((v) => v.action !== 'buy' || v.buySkuId != null, {
    message: 'buySkuId is required when action = buy',
    path: ['buySkuId'],
  });
export type FinancialScenarioCohortReplace = z.infer<typeof financialScenarioCohortReplaceSchema>;

/**
 * Body for `PUT /scenarios/:id` — a whole-scenario replace applied in ONE
 * REQUEST, and therefore one transaction (top-level fields plus the complete
 * cohort set).
 *
 * Exists because the wizard previously orchestrated a save as N independent
 * requests from the browser (PATCH scenario, then a DELETE per removed cohort,
 * then a PATCH or POST per remaining cohort). A failure part-way through left
 * the scenario half-written with no indication of how far it got, and the
 * client had no way to roll back. `cohorts` is REQUIRED here — a replace with
 * an absent cohort array is ambiguous ("leave them alone" or "delete them
 * all"), so callers must be explicit and send `[]` to clear.
 */
export const financialScenarioReplaceSchema = financialScenarioWriteSchema
  .omit({ cohorts: true })
  .extend({ cohorts: z.array(financialScenarioCohortReplaceSchema) });
export type FinancialScenarioReplace = z.infer<typeof financialScenarioReplaceSchema>;

// ---------------------------------------------------------------------------
// 3a-approval. Scenario approval workflow (SIL-568, mig 728, spec §3.2/§6.1)
// ---------------------------------------------------------------------------

/** A stored `scenario_approval_setting` row — one per org, admin-writable
 * only. `capexThresholdUsd` null means approval is never required. */
export const scenarioApprovalSettingRowSchema = z.object({
  id: uuidSchema,
  customerOrgId: uuidSchema,
  capexThresholdUsd: z.coerce.number().nullable(),
  createdBy: uuidSchema.nullable(),
  updatedBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScenarioApprovalSettingRow = z.infer<typeof scenarioApprovalSettingRowSchema>;

/** Body for `PUT /financials/scenario-approval-setting` (admin-only). null
 * clears the threshold (approval never required). */
export const scenarioApprovalSettingWriteSchema = z.object({
  capexThresholdUsd: z.coerce.number().nonnegative().nullable(),
});
export type ScenarioApprovalSettingWrite = z.infer<typeof scenarioApprovalSettingWriteSchema>;

// ---------------------------------------------------------------------------
// 3a-template. Scenario templates (SIL-570, mig 731, spec §3.1/§6.2)
// ---------------------------------------------------------------------------

/**
 * Closed-world rule predicate inside a `scenario_template.cohort_rules`
 * element. `.strict()` — an unrecognized key is a validation error, never
 * silently ignored (spec §6.2's "never a generic Object.entries walk"
 * requirement starts here). `assetClassId` is shared platform reference
 * data and needs no visibility guard. `dataCenterId` is TENANT-SCOPED
 * (data_center is keyed to customer_org_id, 618/623) and carries the same
 * IDOR-guard treatment as `financial_scenario_cohort.selector.assetIds`
 * (681) — resolved only under the caller's own RLS scope, at both template
 * write and instantiation time (apps/api, never validated by shape alone).
 * `ageMonthsMin`/`ageMonthsMax` are accepted here for the rule's documented
 * shape (migration 731's column comment) but resolved at instantiation
 * against `asset.createdAt` as the only per-asset age-adjacent column that
 * exists today — a documented proxy, not a claim of manufacture-date
 * accuracy.
 */
export const scenarioTemplateSelectorRuleSchema = z
  .object({
    assetClassId: z.string().optional(),
    ageMonthsMin: z.number().int().min(0).optional(),
    ageMonthsMax: z.number().int().min(0).optional(),
    dataCenterId: uuidSchema.optional(),
  })
  .strict();
export type ScenarioTemplateSelectorRule = z.infer<typeof scenarioTemplateSelectorRuleSchema>;

/**
 * One `cohort_rules` array element. `action` is validated against the same
 * `scenario_action` enum a live cohort uses — `scenarioActionSchema` now
 * includes `buy` (SIL-571, mig 729/730), so a template rule validates
 * identically whether it was saved before or after that enum landed, no
 * separate template-only allowlist. `refreshSkuId`/`buySkuId` required when
 * `action = 'refresh'`/`'buy'` respectively, mirroring
 * `financialScenarioCohortWriteSchema`'s DB-CHECK-matching refines. A rule
 * never carries `buyUnitPriceUsd`/`buyPriceSuppressed` — those are always
 * server-computed at instantiation (same posture as a live buy cohort,
 * spec §3.4), never stored on the template itself.
 */
export const scenarioTemplateCohortRuleBaseSchema = z
  .object({
    label: z.string().min(1),
    selectorRule: scenarioTemplateSelectorRuleSchema,
    action: scenarioActionSchema,
    timingMonths: z.number().int().min(0),
    refreshSkuId: uuidSchema.optional(),
    buySkuId: uuidSchema.optional(),
  })
  .strict();
export const scenarioTemplateCohortRuleSchema = scenarioTemplateCohortRuleBaseSchema
  .refine((v) => v.action !== 'refresh' || v.refreshSkuId != null, {
    message: 'refreshSkuId is required when action = refresh',
    path: ['refreshSkuId'],
  })
  .refine((v) => v.action !== 'buy' || v.buySkuId != null, {
    message: 'buySkuId is required when action = buy',
    path: ['buySkuId'],
  });
export type ScenarioTemplateCohortRule = z.infer<typeof scenarioTemplateCohortRuleSchema>;

/** A stored `scenario_template` row. Mirrors db.d.ts `ScenarioTemplate`. */
export const scenarioTemplateRowSchema = z.object({
  id: uuidSchema,
  customerOrgId: uuidSchema,
  name: z.string().min(1),
  horizonMonths: scenarioHorizonMonthsSchema,
  cohortRules: z.array(scenarioTemplateCohortRuleSchema).min(1).max(50),
  createdBy: uuidSchema.nullable(),
  updatedBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScenarioTemplateRow = z.infer<typeof scenarioTemplateRowSchema>;

/** Body for `POST /scenarios/:id/save-as-template` (any member). Structure
 * only — the source scenario's cohort selectors are translated to rules
 * server-side; this body carries just the new template's identity. */
export const scenarioSaveAsTemplateRequestSchema = z.object({
  name: z.string().min(1),
});
export type ScenarioSaveAsTemplateRequest = z.infer<typeof scenarioSaveAsTemplateRequestSchema>;

/** Body for `PATCH /scenario-templates/:id` (any member, rename only in
 * v1 — no in-place rule editing surface yet, matches the "mutable single
 * row, no version history" v1 scope). */
export const scenarioTemplatePatchSchema = z.object({
  name: z.string().min(1).optional(),
});
export type ScenarioTemplatePatch = z.infer<typeof scenarioTemplatePatchSchema>;

/** Body for `POST /scenario-templates/:id/instantiate` (any member). */
export const scenarioTemplateInstantiateRequestSchema = z.object({
  name: z.string().min(1),
});
export type ScenarioTemplateInstantiateRequest = z.infer<typeof scenarioTemplateInstantiateRequestSchema>;

/**
 * Response for a successful instantiate. `zeroMatchCohortLabels` names any
 * rule that matched zero assets under the caller's own org at instantiation
 * time (spec §3.1's "creates anyway, with a consequence-first banner" —
 * distinguished from a foreign `dataCenterId`, which 400s the whole request
 * instead of appearing here).
 */
export const scenarioTemplateInstantiateResponseSchema = z.object({
  scenario: financialScenarioRowSchema,
  zeroMatchCohortLabels: z.array(z.string()),
});
export type ScenarioTemplateInstantiateResponse = z.infer<typeof scenarioTemplateInstantiateResponseSchema>;

// ---------------------------------------------------------------------------
// 3b. Scenario compare (T10b) — locked response contract, spec §3.5
// ---------------------------------------------------------------------------

/**
 * SIL-436 (W3, spec §3/§11 step 4) — transient BUY cohort input for the
 * carbon lens only: `buy` is not a first-class dollar `ScenarioAction` yet
 * (see `scenario-carbon-kernel.ts`'s module header — wiring it into the
 * persisted `financial_scenario_cohort.action` enum is a later ticket), so a
 * hypothetical buy is accepted here as a REQUEST-scoped input, never
 * persisted, mirroring how `kwhUsd`/`pue`/etc above are transient overrides.
 * "Buy action = catalog SKU + unit count + new/used + timing + target
 * location" (sustainability-forecasting-map.md). `skuId` is validated against
 * `v_sku_carbon_footprint_latest` at compute time (400 if it doesn't
 * resolve) — see `apps/api/src/lib/scenario-carbon-compare.ts`.
 */
export const carbonBuyCohortInputSchema = z.object({
  label: z.string().min(1),
  skuId: uuidSchema,
  unitCount: z.number().int().positive(),
  condition: z.enum(['new', 'used']),
  timingMonths: z.number().int().min(0),
  /** Boavizta trigram (e.g. 'USA') — the WOR "no location" floor is rejected
   * at compute time (spec §3 "moves to 'No location' disallowed" — the same
   * rule applies to a buy's target per `assertMoveTargetLocationAllowed`). */
  targetUsageLocation: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase()),
});
export type CarbonBuyCohortInput = z.infer<typeof carbonBuyCohortInputSchema>;

/**
 * SIL-436 — transient MOVE cohort input: relocates an EXISTING selector's
 * assets to a new target location at `timingMonths` (spec §3 "rate switches
 * source->target at m"). Like the buy input above, `move` is not a
 * persisted dollar action in v1 — request-scoped only, dollar series
 * unchanged (spec §3 "dollar series unchanged in v1").
 */
export const carbonMoveCohortInputSchema = z.object({
  label: z.string().min(1),
  selector: scenarioCohortSelectorSchema,
  timingMonths: z.number().int().min(0),
  targetUsageLocation: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase()),
});
export type CarbonMoveCohortInput = z.infer<typeof carbonMoveCohortInputSchema>;

/** Container for the request's transient carbon-only cohorts (spec §3/§11
 * step 4) — both arrays optional/omittable, same as every other
 * `scenarioCompareRequestSchema` field. */
export const carbonScenarioCohortInputsSchema = z.object({
  buy: z.array(carbonBuyCohortInputSchema).optional(),
  move: z.array(carbonMoveCohortInputSchema).optional(),
});
export type CarbonScenarioCohortInputs = z.infer<typeof carbonScenarioCohortInputsSchema>;

/** Transient assumption overrides for "what if I change $/kWh without saving"
 * — body of `POST /scenarios/:id/compare` (spec §6). Every field optional;
 * omitted fields use the saved scenario's stored value. */
export const scenarioCompareRequestSchema = z.object({
  kwhUsd: z.coerce.number().nonnegative().optional(),
  pue: scenarioPueSchema.optional(),
  itadRecoveryPct: z.coerce.number().min(0).max(100).optional(),
  fmvBand: fmvBandSchema.optional(),
  /** SIL-436 — transient hypothetical buy/move cohorts for the carbon lens
   * only (see `carbonScenarioCohortInputsSchema`'s docstring). Additive,
   * optional — a request that omits it computes the carbon block from the
   * scenario's persisted cohorts alone. */
  carbonCohorts: carbonScenarioCohortInputsSchema.optional(),
});
export type ScenarioCompareRequest = z.infer<typeof scenarioCompareRequestSchema>;

/** Per-month metrics for either the baseline or the scenario arm (spec §3.5). */
export const scenarioCompareMetricsPointSchema = z.object({
  recoveredValueUsd: z.coerce.number(),
  residualFmvUsd: z.coerce.number(),
  powerCostUsd: z.coerce.number(),
  refreshCapexUsd: z.coerce.number(),
  netPositionUsd: z.coerce.number(),
});
export type ScenarioCompareMetricsPoint = z.infer<typeof scenarioCompareMetricsPointSchema>;

/** One co-indexed month of the overlay series — baseline + scenario side by
 * side so the frontend does no join (spec §3.5 "lock before T16 builds against it"). */
export const scenarioCompareSeriesPointSchema = z.object({
  month: z.number().int().nonnegative(),
  baseline: scenarioCompareMetricsPointSchema,
  scenario: scenarioCompareMetricsPointSchema,
});
export type ScenarioCompareSeriesPoint = z.infer<typeof scenarioCompareSeriesPointSchema>;

/** End-state summary metrics: baseline vs scenario vs delta. */
export const scenarioCompareSummaryMetricsSchema = z.object({
  baseline: scenarioCompareMetricsPointSchema,
  scenario: scenarioCompareMetricsPointSchema,
  delta: scenarioCompareMetricsPointSchema,
});
export type ScenarioCompareSummaryMetrics = z.infer<typeof scenarioCompareSummaryMetricsSchema>;

/** Wire mirror of the kernel's `CategoryKgCo2ePoint` — one month's (or one
 * cohort's) kgCO2e broken out by GHG category. Never a 6th "advisory"
 * bucket (kernel module header) — the advisory panel is a SEPARATE response
 * field (`carbonAdvisory` below), never merged into this shape. Hoisted
 * above `scenarioCompareByCohortSchema` (SIL-517) so the per-cohort
 * `carbonSeries` field below can reference it. */
export const categoryKgCo2ePointSchema = z.object({
  scope2: z.coerce.number(),
  cat8: z.coerce.number(),
  cat1_or_2: z.coerce.number(),
  cat5: z.coerce.number(),
  cat4: z.coerce.number(),
});
export type CategoryKgCo2ePointDto = z.infer<typeof categoryKgCo2ePointSchema>;

/** One co-indexed month of the carbon series — SAME index range (0..
 * horizonMonths) and SAME month values as `series` above, so a caller zips
 * `carbon.series[i]` with `series[i]` without realignment (kernel's
 * `CarbonScenarioSeriesPoint` docstring, design constraint #3). */
export const carbonScenarioSeriesPointDtoSchema = z.object({
  month: z.number().int().nonnegative(),
  baseline: categoryKgCo2ePointSchema,
  scenario: categoryKgCo2ePointSchema,
  delta: categoryKgCo2ePointSchema,
});
export type CarbonScenarioSeriesPointDto = z.infer<typeof carbonScenarioSeriesPointDtoSchema>;

/** Additive per-cohort drill-down slot (spec §3.5 "additive slot for later"),
 * extended SIL-517 with genuine per-cohort monthly series so the Compare
 * step's time-Sankeys never assume an equal-share split across cohorts (the
 * validated prototype's known limitation — see scenario-engine.ts). Both new
 * fields are optional/additive: a caller that doesn't ask for the Sankeys
 * still gets the pre-existing endpoint-only `metrics`. */
export const scenarioCompareByCohortSchema = z.object({
  cohortId: uuidSchema,
  label: z.string(),
  /** Epic 2 (lots-first-class) — this cohort's LIVE member count, summed
   * across every asset class `resolveCohortClassAggregates` returned for
   * it, at the SAME `generatedAt` moment as the rest of this response.
   * Resolution is live (matches `assetClassId`'s existing posture) and
   * nothing about a lot/team cohort's MEMBERSHIP is persisted, so this
   * number can silently change between saves — the customer UI renders it
   * next to `generatedAt` for exactly that reason (see
   * plans/lots-first-class.md Epic 2 "member count and an as-of
   * timestamp"). Present for every cohort, not just lot/team-scoped ones —
   * cheap (already computed) and uniform beats a conditional field. */
  assetCount: z.number().int().nonnegative(),
  metrics: scenarioCompareSummaryMetricsSchema,
  /** SIL-517 — this cohort's own co-indexed monthly value series (SAME month
   * range/index as the top-level `series`), straight from `scenarioEngine`'s
   * per-cohort compute (`computeScenarioCompare` already ran it for
   * `metrics` above; this just keeps the discarded `.series` too). */
  series: z.array(scenarioCompareSeriesPointSchema).optional(),
  /** SIL-517 — this cohort's own co-indexed monthly carbon series
   * (category-tagged, SAME month range as `carbon.series`), from re-running
   * the SIL-435 kernel over just this cohort's assembled kernel cohort(s).
   * Absent for a cohort the carbon assembly skipped entirely (e.g. zero
   * matching assets) — absence means "not computed," never a silent zero. */
  carbonSeries: z.array(carbonScenarioSeriesPointDtoSchema).optional(),
});
export type ScenarioCompareByCohort = z.infer<typeof scenarioCompareByCohortSchema>;

/**
 * SIL-346 (T8) — one cohort's use-phase carbon projection row. Branded
 * `ProjectedUsePhaseKgCo2e` end to end (never unbranded to a plain
 * `number`) — see `use-phase/scenario-use-phase.ts`'s module header on why
 * this is a mechanical, not review-only, guard against the figure ever
 * reaching the ESG export.
 */
export const scenarioUsePhaseByCohortSchema = z.object({
  cohortId: uuidSchema,
  label: z.string(),
  /** Cohort's summed DEVICE-level annual rate (kgCO2e/yr, PUE not yet
   * applied) — disclosed so a reviewer can hand-check `projectedKgco2e`
   * against `deviceAnnualRateKgco2eqPerYr * pue * (carbonAccrualStopMonth / 12)`. */
  deviceAnnualRateKgco2eqPerYr: z.coerce.number(),
  /**
   * Month (0..horizonMonths) this cohort's rate stops accruing —
   * `horizonMonths` itself for `hold`; `min(timingMonths, horizonMonths)`
   * for `sell`/`itad`/`refresh` (the operator no longer purchases this
   * hardware's electricity once it leaves their operational control).
   */
  carbonAccrualStopMonth: z.number().int().nonnegative(),
  /**
   * True only for a `refresh` cohort whose action falls inside the
   * horizon — `projectedKgco2e` covers only the OLD fleet up to
   * `carbonAccrualStopMonth`; the remainder has no rate to project (the
   * replacement SKU owns no assets yet, hence no `asset_use_phase_snapshot`)
   * and is intentionally NOT included as either a zero or the old rate
   * carried forward. See the top-level `basis`'s `postRefreshCarbon:*`
   * input for the disclosed gap.
   */
  carbonPostActionUnmodeled: z.boolean(),
  projectedKgco2e: projectedUsePhaseKgco2eqSchema,
});
export type ScenarioUsePhaseByCohort = z.infer<typeof scenarioUsePhaseByCohortSchema>;

/**
 * SIL-346 (T8) — the scenario-wide use-phase carbon projection: the stored
 * annual rate(s) prorated over `horizonMonths`, PUE applied at read time.
 * Explicitly labeled a FORECAST, not an inventory figure (plan §"Financial
 * scenario" surface row) — this DTO must never be reachable from the ESG
 * CSV export (`routes/client/*export*.ts`'s `no-restricted-imports` fence,
 * SIL-345).
 */
export const scenarioUsePhaseProjectionSchema = z.object({
  /** Scenario-wide total across every cohort — sum of `byCohort[].projectedKgco2e`. */
  projectedKgco2e: projectedUsePhaseKgco2eqSchema,
  /** The PUE actually applied — the scenario's own (validated `[1.0, 3.0]`)
   * value, or the published industry default when unset. */
  pue: z.coerce.number(),
  pueSource: z.enum(['manual_entry', 'platform_default']),
  byCohort: z.array(scenarioUsePhaseByCohortSchema),
  basis: calculationBasisSchema,
});
export type ScenarioUsePhaseProjection = z.infer<typeof scenarioUsePhaseProjectionSchema>;

// ---------------------------------------------------------------------------
// 3b-ii. Carbon block (SIL-436, W3 of sustainability-forecasting-spec.md
//    §3/§5/§11 step 4) — the category-tagged carbon series + firewalled
//    advisory panel wired from `use-phase/scenario-carbon-kernel.ts` (SIL-435,
//    W2, already merged, arithmetic never re-implemented here). Distinct from
//    `useCarbon`/`scenarioUsePhaseProjectionSchema` above (SIL-346, an older
//    single-total device-rate x PUE projection) — this is the newer,
//    category-tagged, baseline/scenario/delta series the two-panel firewall
//    (spec §5) requires.
// ---------------------------------------------------------------------------

/** The carbon block's own `CalculationBasis`-equivalent — the SIL-426 §14
 * per-cohort rate-source + scalar disclosures, translated into the shared
 * `CalculationBasis` shape (see
 * `packages/shared/src/use-phase/scenario-carbon-basis.ts`'s
 * `translateCarbonKernelBasis` — extends `basisSourceSchema` above with
 * carbon-specific sources per spec §11 step 4). One basis for the whole
 * carbon block (cardinality: "one basis for totals" — same convention as
 * the dollar-side `basis` field), not one per cohort. */
export const scenarioCarbonBlockSchema = z.object({
  series: z.array(carbonScenarioSeriesPointDtoSchema),
  basis: calculationBasisSchema,
});
export type ScenarioCarbonBlock = z.infer<typeof scenarioCarbonBlockSchema>;

/** Wire mirror of the kernel's `AdvisoryKgCo2e` unwrap — a plain `{kgCo2e}`
 * shape ONLY inside `carbonAdvisory` below, never assignable into
 * `categoryKgCo2ePointSchema`'s fields (the firewall is structural in the
 * kernel; this DTO just keeps the two response fields separate, per spec §5
 * "never summed, netted, or shared an axis with the footprint"). */
export const advisoryCohortEntrySchema = z.object({
  cohortLabel: z.string(),
  kgCo2e: z.coerce.number(),
});
export type AdvisoryCohortEntry = z.infer<typeof advisoryCohortEntrySchema>;

/**
 * The three advisory numbers (spec §5, SIL-424 #6 + SIL-426 fixes), each an
 * array because more than one buy/sell/itad cohort can contribute one entry
 * apiece. A SEPARATE top-level response field from `carbon` above — the
 * series can never sum with this (spec: "the series can never sum with the
 * advisory numbers"). Downstream reuse is "shown by default" (SIL-426 §10)
 * — still returned as an array here; the UI's default-visibility choice is
 * a rendering concern, not a compute one.
 */
export const carbonAdvisoryPanelSchema = z.object({
  avoidedVsNew: z.array(advisoryCohortEntrySchema),
  disposalDeferred: z.array(advisoryCohortEntrySchema),
  downstreamReuse: z.array(advisoryCohortEntrySchema),
});
export type CarbonAdvisoryPanel = z.infer<typeof carbonAdvisoryPanelSchema>;

// ---------------------------------------------------------------------------
// 3b-iii. Carbon sensitivity tornado (SIL-443, W5 of
//    sustainability-forecasting-spec.md §11 "ship last") — one-at-a-time
//    perturbation of the scenario's CARBON drivers over the same SIL-435
//    kernel the `carbon` block above was built from
//    (`use-phase/carbon-sensitivity.ts`). FOOTPRINT DELTAS ONLY: every figure
//    here derives from `carbon.series[last].delta`; the `carbonAdvisory`
//    numbers are structurally excluded and must never be added.
// ---------------------------------------------------------------------------

/** Closed union matching `CARBON_SENSITIVITY_ATTRIBUTES`'s literal `id`s —
 * the same "no registry framing" posture as `sensitivityAttributeIdSchema`
 * (dto/scenario-sensitivity.ts). */
export const carbonSensitivityAttributeIdSchema = z.enum([
  'grid_intensity',
  'pue',
  'disposal_weee_factor',
  'used_transport_km',
  'used_refurb_pct',
]);
export type CarbonSensitivityAttributeIdDto = z.infer<typeof carbonSensitivityAttributeIdSchema>;

/**
 * One carbon tornado row. `applicable: false` ALWAYS carries a non-null
 * `reason` and null bounds — never a disguised zero (the dollar tornado's
 * "a 0 swing must never read as insensitive" rule, restated for carbon). A
 * driver a scenario genuinely carries but that happens to swing 0 stays
 * `applicable: true` with `swingKgco2e: 0` — a true, disclosed zero.
 */
export const carbonSensitivityAttributeResultSchema = z.object({
  id: carbonSensitivityAttributeIdSchema,
  label: z.string(),
  applicable: z.boolean(),
  reason: z.string().nullable(),
  /** Horizon-end footprint delta (scenario minus baseline, all five GHG
   * categories summed, kgCO2e) at the unperturbed drivers — the shared
   * centre line. */
  baseDeltaKgco2e: z.coerce.number(),
  lowDeltaKgco2e: z.coerce.number().nullable(),
  highDeltaKgco2e: z.coerce.number().nullable(),
  /** `high - low`, signed. Null iff `applicable` is false. */
  swingKgco2e: z.coerce.number().nullable(),
  /** The multipliers applied to the driver (e.g. 0.7 / 1.3), echoed so the
   * UI can state the tested range without re-deriving it from constants. */
  lowMultiplier: z.coerce.number().nullable(),
  highMultiplier: z.coerce.number().nullable(),
  basis: calculationBasisSchema,
});
export type CarbonSensitivityAttributeResult = z.infer<typeof carbonSensitivityAttributeResultSchema>;

/** `.max(8)` shapes for future drivers (freight factor, a per-location grid
 * lens) without widening the contract yet — same convention as
 * `scenarioSensitivityResponseSchema`'s `.max(6)`. */
export const carbonSensitivityBlockSchema = z.object({
  attributes: z.array(carbonSensitivityAttributeResultSchema).max(8),
});
export type CarbonSensitivityBlock = z.infer<typeof carbonSensitivityBlockSchema>;

/** Response for `POST /scenarios/:id/compare` — locked contract per spec §3.5. */
export const scenarioCompareResponseSchema = z.object({
  scenarioId: uuidSchema,
  horizonMonths: scenarioHorizonMonthsSchema,
  generatedAt: z.string(),
  series: z.array(scenarioCompareSeriesPointSchema),
  metrics: scenarioCompareSummaryMetricsSchema,
  byCohort: z.array(scenarioCompareByCohortSchema).optional(),
  /**
   * SIL-297 (W2) CalculationBasis retrofit — Level-2 basis disclosure for
   * the scenario-wide assumptions ($/kWh, PUE) every cohort's compute uses.
   * Optional — strictly additive per spec Cross-cutting. A single
   * response-level basis (spec cardinality: "one basis for totals") — a
   * cohort's own class-level FMV/ITAD basis is disclosed by
   * `GET /financials/summary` / `/opportunity`, not duplicated per cohort
   * here.
   */
  basis: calculationBasisSchema.optional(),
  /** SIL-346 (T8) — additive, optional (a scenario whose cohorts resolve
   * zero use-phase snapshots still gets a `useCarbon` block, all-zero, not
   * an absent field — "never drop assets" applies here too: absence would
   * read as "not computed," not "zero"). */
  useCarbon: scenarioUsePhaseProjectionSchema.optional(),
  /**
   * SIL-351 — count of assets across every cohort whose wattage `basis` was
   * 'unavailable' (no telemetry, no `sku_oem_spec` power field, no GPU-TDP
   * fallback possible). These assets contribute 0 to `powerCostUsd`, NOT
   * because they measured 0W, but because there is no signal at all — a
   * disclosed gap, never a silent confident $0. Optional — additive per
   * spec Cross-cutting; absent/0 both mean "no known gap" for a caller that
   * doesn't render this yet.
   */
  wattsUnavailableAssetCount: z.number().int().min(0).optional(),
  /**
   * SIL-436 (W3) — the category-tagged carbon series co-indexed with
   * `series` above, wired from the SIL-435 kernel. Optional/additive, same
   * "never drop, always compute" posture as `useCarbon`: a scenario with no
   * carbon-relevant cohorts still gets an all-zero `carbon` block, not an
   * absent field.
   */
  carbon: scenarioCarbonBlockSchema.optional(),
  /**
   * SIL-436 (W3) — the three firewalled advisory numbers (spec §5), in a
   * SEPARATE field from `carbon` so they can never be summed with the
   * official footprint series. Optional/additive.
   */
  carbonAdvisory: carbonAdvisoryPanelSchema.optional(),
  /**
   * SIL-443 (W5) — the carbon tornado, computed off the SAME assembled
   * cohorts `carbon` above was built from (so a bar can never disagree with
   * the chart it sits under). Rides the compare response rather than a
   * sibling `GET /scenarios/:id/carbon-sensitivity` route deliberately: the
   * drivers are only resolvable alongside the carbon block's own assembly
   * (grid factors, org scalars, transient buy/move cohorts that exist
   * request-scoped only and are NOT persisted), so a separate GET could not
   * see the buy/move hypotheticals at all and would silently report a
   * different scenario. Optional/additive, same posture as `carbon`.
   */
  carbonSensitivity: carbonSensitivityBlockSchema.optional(),
  /**
   * SIL-571 — buy cohorts EXCLUDED from this response's economics because
   * their `buyPriceSuppressed` flag is true (spec §3.4: "can't price this
   * purchase — insufficient market depth"). A separate, explicitly-named
   * list rather than a $0 line in `series`/`metrics`/`byCohort` — a
   * suppressed cohort contributes NOTHING to those (not a real, disclosed
   * zero), so the reader must see the exclusion named here to tell "excluded,
   * unpriceable" apart from "genuinely free". Optional/additive; empty array
   * (not absent) when every cohort priced or none is a buy cohort.
   */
  excludedCohorts: z
    .array(
      z.object({
        cohortId: uuidSchema,
        label: z.string(),
        reason: z.string(),
      }),
    )
    .optional(),
  /**
   * SIL-1828 (R2): true when any cohort's FMV starts from a virtual
   * (sum-of-components) baseline. Every figure is shown but must carry the
   * "Directional" label. Server-derived; UIs never re-derive. Optional so
   * persisted compareResult snapshots written before this still parse.
   */
  includesDirectionalBaseline: z.boolean().optional(),
});
export type ScenarioCompareResponse = z.infer<typeof scenarioCompareResponseSchema>;

// ---------------------------------------------------------------------------
// 3b-iv. Approval snapshot (scenario freeze-on-approval, migration 748) — the
//    immutable, versioned freeze of a scenario's ScenarioCompareResult at the
//    moment an admin approved it (POST /:id/approve), plus the assumption
//    chips (horizonMonths/fmvBand/kwhUsd/pue/itadRecoveryPct) as they stood
//    then — mutable on the parent scenario, and editing them un-approves it
//    (728's financial_scenario_lock_trg), so freezing them here keeps the
//    frozen dollars and the assumptions they were computed from from ever
//    drifting apart on the detail page.
// ---------------------------------------------------------------------------

/** One immutable approval snapshot row. `compareResult` is the FULL
 * ScenarioCompareResult, already validated against
 * `scenarioCompareResponseSchema` at write time (the same contract
 * `/compare`'s live response satisfies) — this schema re-validates it on the
 * read path too, so a caller trusting this DTO is trusting the same shape
 * either way. */
export const financialScenarioApprovalSnapshotSchema = z.object({
  id: uuidSchema,
  scenarioId: uuidSchema,
  version: z.number().int().positive(),
  approvedBy: uuidSchema,
  /** When the admin clicked approve. */
  approvedAt: z.string(),
  /** When the compute actually ran (compareResult.generatedAt) — distinct
   * from approvedAt, never conflated with it. */
  generatedAt: z.string(),
  horizonMonths: scenarioHorizonMonthsSchema,
  fmvBand: fmvBandSchema,
  kwhUsd: z.coerce.number(),
  pue: z.coerce.number().nullable(),
  itadRecoveryPct: z.coerce.number(),
  approvedCapexUsd: z.coerce.number(),
  approvedThresholdUsd: z.coerce.number().nullable(),
  compareResult: scenarioCompareResponseSchema,
});
export type FinancialScenarioApprovalSnapshot = z.infer<typeof financialScenarioApprovalSnapshotSchema>;

/**
 * Response for `GET /scenarios/:id/approval-snapshot`. A discriminated union
 * so the client can tell THREE states apart — critically, "approved before
 * snapshots existed" (pre-748 approval, no row here) must NEVER silently
 * fall back to a live recompute, which is the exact bug this feature fixes
 * one level down:
 *   - `not_approved`   — the scenario's current status isn't 'approved' at
 *     all (draft/in_review, or was approved and later un-approved).
 *   - `available`      — approved AND a frozen snapshot exists; `snapshot`
 *     is the source of truth for the Compare & decide page's frozen figures.
 *   - `unavailable`    — the scenario IS currently approved, but no snapshot
 *     row exists for it (approved before migration 748 shipped — explicitly
 *     NOT backfilled, since a backfill would recompute against TODAY's
 *     market comps/asset states and stamp a historical approvedAt on
 *     essentially live data, fabricating a "frozen" artifact). The client
 *     must render this as a disclosed gap ("no frozen record for this
 *     approval"), not a live recompute standing in for one.
 */
export const financialScenarioApprovalSnapshotResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('not_approved') }),
  z.object({ status: z.literal('available'), snapshot: financialScenarioApprovalSnapshotSchema }),
  z.object({ status: z.literal('unavailable') }),
]);
export type FinancialScenarioApprovalSnapshotResponse = z.infer<
  typeof financialScenarioApprovalSnapshotResponseSchema
>;

// ---------------------------------------------------------------------------
// 3c. Refresh candidate SKUs (T16) — Refresh-step candidate table
// ---------------------------------------------------------------------------

/**
 * One refresh candidate SKU (spec §3.5/§9 Wave 4 T16 "Refresh step shows a
 * class-filtered candidate-SKU table"). `consolidationRatio` mirrors the
 * SAME default formula `apps/api/src/lib/scenario-compare.ts`'s
 * `buildCohortInputs` applies at compute time (fp16-TFLOPS ratio for
 * `gpu_server`-to-`gpu_server` refreshes, 1 elsewhere / when either side's
 * fp16 figure is unknown) — a preview, not a separate source of truth; the
 * saved cohort's `consolidationRatio` (or the server-computed default) is
 * still what `/compare` actually uses.
 */
export const refreshCandidateSkuSchema = z.object({
  skuId: uuidSchema,
  make: z.string().nullable(),
  model: z.string().nullable(),
  /**
   * Manufacturer-FREE display name: `model` with the leading manufacturer
   * removed (`stripManufacturerPrefix`, SIL-180). `model` (i.e. `model_name`)
   * repeats the manufacturer, so a picker that renders `make` in its own
   * element and then `model` shows it twice ("Dell Technologies DELL
   * TECHNOLOGIES POWEREDGE R750XA"). Render `make` + `displayName`. A model
   * that does not start with its manufacturer comes through whole; null only
   * when the row has no `model_name` at all.
   */
  displayName: z.string().nullable(),
  fp16Tflops: z.coerce.number().nullable(),
  tdpWatts: z.coerce.number().nullable(),
  consolidationRatio: z.coerce.number().nullable(),
});
export type RefreshCandidateSku = z.infer<typeof refreshCandidateSkuSchema>;

/** Response for `GET /scenarios/refresh-candidates` (T16). */
export const refreshCandidatesResponseSchema = z.object({
  assetClassId: z.string(),
  /** The cohort-selector's current average fp16 TFLOPS (null when the class
   * has no fp16-speced assets, or the class isn't a GPU class) — the
   * denominator behind each candidate's `consolidationRatio`. */
  avgFp16Tflops: z.coerce.number().nullable(),
  candidates: z.array(refreshCandidateSkuSchema),
});
export type RefreshCandidatesResponse = z.infer<typeof refreshCandidatesResponseSchema>;

/** Query for `GET /scenarios/refresh-candidates` — mirrors a cohort's
 * selector shape (spec §2.3) so the Refresh step can preview candidates
 * against an UNSAVED cohort's current selector. */
export const refreshCandidatesQuerySchema = z.object({
  assetClassId: z.string().min(1),
  assetIds: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').filter(Boolean) : undefined))
    .pipe(z.array(uuidSchema).optional()),
});
export type RefreshCandidatesQuery = z.infer<typeof refreshCandidatesQuerySchema>;

// ---------------------------------------------------------------------------
// 4a. Impairment watchlist (SIL-296, W2, spec slice 5) — trailing 6-quarter
//     class FMV-vs-NBV. Declared ahead of the "4. Report rows" section below
//     because `bookVsFmvSummaryResponseSchema` embeds `impairmentWatchlistSchema`
//     and Zod schemas are plain `const`s evaluated in module order.
// ---------------------------------------------------------------------------

/** Tier thresholds live in `financials-constants.ts` (WATCH/REVIEW/LIKELY %
 * + min-consecutive-quarters) — this enum is just the closed set of tier
 * names `classifyImpairmentTier` (impairment-watchlist.ts) can return.
 * `none` = FMV at or above net book (not on the watchlist at all). */
export const impairmentTierSchema = z.enum(['none', 'watch', 'review', 'likely']);
export type ImpairmentTier = z.infer<typeof impairmentTierSchema>;

/** One class's current (most recent trailing quarter) watchlist standing.
 * `quarterEnd` is that quarter's last calendar day (matches migration 700's
 * `v_client_class_impairment_watchlist.quarter_end`). `copy`/`evidenceCopy`
 * are pre-rendered factual strings (spec: "shipped copy stays factual ...
 * ASC 360 framing lives in a tooltip") — the UI issue renders them verbatim,
 * this DTO is the single source of the wording so the API and any export
 * (CSV/PDF) never drift. */
export const impairmentWatchlistClassRowSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  quarterEnd: z.string(),
  fmvMedUsd: z.coerce.number().nullable(),
  netBookValueUsd: z.coerce.number().nullable(),
  /** fmvMedUsd - netBookValueUsd (negative = FMV below book). */
  gapUsd: z.coerce.number().nullable(),
  /** % below book, positive when FMV < NBV (null when NBV <= 0). */
  gapPct: z.coerce.number().nullable(),
  consecutiveQuartersBelow: z.number().int().nonnegative(),
  tier: impairmentTierSchema,
  /** Factual, e.g. "FMV 35% below net book for 3 quarters". */
  copy: z.string(),
  /** "needs evidence from N consecutive quarters" when the tier is below
   * REVIEW and could still be reached via the streak-alone path; null once
   * the class has already cleared the streak requirement for its tier. */
  evidenceCopy: z.string().nullable(),
  /**
   * Level-2 basis disclosure (spec Cross-cutting) — discloses the two
   * simplifications spec §5 calls out explicitly: ownership rollup under the
   * CURRENT owner, and TODAY's resolved policy applied retroactively to a
   * past quarter (both surfaced as `isFallback: true` inputs, never silent).
   * Optional — strictly additive.
   */
  basis: calculationBasisSchema.optional(),
});
export type ImpairmentWatchlistClassRow = z.infer<typeof impairmentWatchlistClassRowSchema>;

/** Retrofit payload for `GET /financials/summary` (spec slice 5: "watchlist
 * rows ... + hero 'gap widening ~$X/quarter' stat from the same series").
 * `classes` includes ONLY classes currently on the watchlist (tier !==
 * 'none') — a class with FMV at or above book never appears here. */
export const impairmentWatchlistSchema = z.object({
  classes: z.array(impairmentWatchlistClassRowSchema),
  /**
   * Portfolio-wide hero stat: the trailing series' linear gap_usd slope,
   * USD per quarter. Negative = the FMV-below-book shortfall is WIDENING
   * (getting more negative) quarter over quarter; positive = narrowing.
   * Null when fewer than 2 quarters have any class data (no slope
   * computable) — never a fabricated zero.
   */
  gapWideningUsdPerQuarter: z.coerce.number().nullable(),
  generatedAt: z.string(),
});
export type ImpairmentWatchlist = z.infer<typeof impairmentWatchlistSchema>;

// ---------------------------------------------------------------------------
// 4. Report rows (T7) — Book vs FMV, depreciation schedule, opportunity-cost
// ---------------------------------------------------------------------------

/** One point of an 18-month FMV trend sparkline (spec §4 "By-Asset-Class table"). */
export const fmvTrendPointSchema = z.object({
  month: z.string(),
  fmvMedUsd: z.coerce.number(),
});
export type FmvTrendPoint = z.infer<typeof fmvTrendPointSchema>;

/** Per-class row of the Book vs FMV summary (`GET /financials/summary`, T7).
 * Epic 3 (lots-first-class) — generalized to a per-GROUP row: `groupId`/
 * `groupLabel` are populated for every `dimension` (class/lot/team/site);
 * `assetClassId`/`assetClassName` stay populated ONLY when `dimension ===
 * 'class'` (null otherwise — a lot/team/site group spans multiple classes,
 * so there is no single class to report). `groupId === null` is the explicit
 * "Unassigned" bucket for the `lot`/`site` dimensions (never a real class or
 * team — team is never null, migration 737/738). */
export const bookVsFmvClassRowSchema = z.object({
  assetClassId: z.string().nullable(),
  assetClassName: z.string().nullable(),
  /** The group's identifying key for the active `dimension` — the class id,
   * lot id, team id, or data_center id. Null only for the lot/site
   * "Unassigned" bucket. */
  groupId: z.string().nullable(),
  /** Always-present display label, e.g. an asset class name, a lot name, a
   * team name, a data center name, or "Unassigned". */
  groupLabel: z.string(),
  assetCount: z.number().int().nonnegative(),
  netBookValueUsd: z.coerce.number().nullable(),
  fmvMedUsd: z.coerce.number().nullable(),
  /** fmvMedUsd - netBookValueUsd. */
  unrealizedGainLossUsd: z.coerce.number().nullable(),
  itadBaselineUsd: z.coerce.number().nullable(),
  /** fmvMedUsd - itadBaselineUsd — the "cost of not deciding" headline (§3.3). */
  opportunityCostUsd: z.coerce.number().nullable(),
  trend: z.array(fmvTrendPointSchema),
  /** True if any asset in the class is on an estimated (non-purchase-price) book value. */
  isEstimate: z.boolean(),
  policyTier: groupPolicyTierSchema,
  /**
   * SIL-291 (W0) reference retrofit — Level-2 basis disclosure for this
   * class's book value (gross book value source/fallback, policy tier,
   * weighted age). Optional — strictly additive per spec Cross-cutting.
   * Attached per class row (not on `totals`) because its genuinely
   * class-differing input is `policyTier` (spec: "one per class row only
   * where inputs genuinely differ by class").
   */
  basis: calculationBasisSchema.optional(),
});
export type BookVsFmvClassRow = z.infer<typeof bookVsFmvClassRowSchema>;

/** Response for `GET /financials/summary` (T7). */
export const bookVsFmvSummaryResponseSchema = z.object({
  classes: z.array(bookVsFmvClassRowSchema),
  /** Epic 3 (lots-first-class) — which dimension `classes` is grouped by.
   * Defaults to `'class'` (the original, unparameterized shape) when the
   * request omits `?dimension=`. */
  dimension: financialsGroupDimensionSchema,
  totals: z.object({
    netBookValueUsd: z.coerce.number().nullable(),
    fmvMedUsd: z.coerce.number().nullable(),
    unrealizedGainLossUsd: z.coerce.number().nullable(),
    itadBaselineUsd: z.coerce.number().nullable(),
    opportunityCostUsd: z.coerce.number().nullable(),
  }),
  /** Drives the dismissible "using org default policy" banner (spec §4). */
  usingOrgDefaultPolicy: z.boolean(),
  generatedAt: z.string(),
  /** SIL-296 (W2) retrofit — impairment watchlist rows + hero gap-widening
   * stat, derived from the SAME per-class book/FMV data this response
   * already assembles (spec Cross-cutting "aggregate stays aggregate").
   * Optional — strictly additive. */
  impairmentWatchlist: impairmentWatchlistSchema.optional(),
});
export type BookVsFmvSummaryResponse = z.infer<typeof bookVsFmvSummaryResponseSchema>;

/** Per-asset drill-down row for `GET /financials/summary?assetClassId=&cursor=`. */
export const bookVsFmvAssetRowSchema = z.object({
  assetId: uuidSchema,
  assetClassId: z.string(),
  serialNumber: z.string().nullable(),
  netBookValueUsd: z.coerce.number().nullable(),
  fmvMedUsd: z.coerce.number().nullable(),
  isEstimate: z.boolean(),
  policyTier: policyTierSchema,
  /** ERP register import epic (SIL-271, spec decision 5) — the ERP's own
   * reported net book value as of `erpNbvAsOfDate`, shown ONLY as a variance
   * against our computed `netBookValueUsd`/`fmvMedUsd` (never an input to
   * the policy engine). Null when no ERP register row has ever been
   * promoted for this asset. */
  erpReportedNbvUsd: z.coerce.number().nullable(),
  erpNbvAsOfDate: z.string().nullable(),
});
export type BookVsFmvAssetRow = z.infer<typeof bookVsFmvAssetRowSchema>;

/** Paginated per-asset drill-down response (T7). */
export const bookVsFmvAssetsResponseSchema = z.object({
  assets: z.array(bookVsFmvAssetRowSchema),
  pageMeta: pageMetaSchema,
});
export type BookVsFmvAssetsResponse = z.infer<typeof bookVsFmvAssetsResponseSchema>;

// ---------------------------------------------------------------------------
// 5. Asset Financials report (SIL-573, I2-D1, wayfinder-iter2-spec.md §5,
//    §7-D1) — a fleet-wide (not per-class) per-asset financial table. Every
//    column is a real read from data already backing the Financial Position
//    report above: `v_client_asset_book_value` (682) for book value +
//    accumulated depreciation, `asset_financial` for the reported acquisition
//    cost/date and ERP match source, `fmv_pricing_snapshot` for the current
//    FMV band. Honest-data gate (spec §5): no column ships without a real
//    source, so there is no "ERP method" or "useful life" column here — those
//    live on the Depreciation Schedule / Capitalization Policy screens.
// ---------------------------------------------------------------------------

/** One row of the fleet-wide Asset Financials table (`GET
 * /financials/asset-financials`). Unlike `bookVsFmvAssetRowSchema` (which is
 * scoped to a single asset class via `?assetClassId=`), this endpoint spans
 * every asset the org owns — the table's whole reason to exist is a single
 * per-asset view across the fleet, not a class drill-down. */
export const assetFinancialsRowSchema = z.object({
  assetId: uuidSchema,
  assetClassId: z.string(),
  assetClassName: z.string().nullable(),
  serialNumber: z.string().nullable(),
  /** Reported fact from `asset_financial.purchase_price_usd` — NOT the
   * book-value engine's gross book value, which falls back to the SKU's
   * launch list price when this is absent (see `isEstimate`). Null when no
   * asset_financial row exists for this asset at all. */
  acquisitionCostUsd: z.coerce.number().nullable(),
  /** `asset_financial.purchased_at` — the reported acquisition date. */
  acquisitionDate: z.string().nullable(),
  /** `v_client_asset_book_value.net_book_value_usd`. Null when the class has
   * no depreciation policy resolved (`policyTier === 'no_policy'`) or the
   * asset has no in-service date. */
  bookValueUsd: z.coerce.number().nullable(),
  /** True when `bookValueUsd`'s GROSS input fell back to the SKU launch
   * price because `acquisitionCostUsd` is null (spec: book value can be an
   * estimate even when this row shows a real acquisition cost is absent). */
  isEstimate: z.boolean(),
  policyTier: policyTierSchema,
  /** `v_client_asset_book_value.accumulated_depreciation_usd` —
   * grossBookValueUsd - netBookValueUsd, same null conditions as bookValueUsd. */
  depreciationToDateUsd: z.coerce.number().nullable(),
  /** Current FMV band from the asset's latest `fmv_pricing_snapshot` — low,
   * med, and high, not a single point estimate (spec: "current FMV band"). */
  fmvLowUsd: z.coerce.number().nullable(),
  fmvMedUsd: z.coerce.number().nullable(),
  fmvHighUsd: z.coerce.number().nullable(),
  /** fmvMedUsd - bookValueUsd. Null unless BOTH operands are present — never
   * a partial computation. */
  bookVsFmvDeltaUsd: z.coerce.number().nullable(),
  /** `asset_financial.source` — 'manual' | 'csv' | 'erp'. Null when no
   * asset_financial row exists for this asset (unmatched, not a 4th enum
   * value — honest-data gate: the UI renders this as "No data", never a
   * stubbed status). */
  erpMatchStatus: financialSourceSchema.nullable(),
});
export type AssetFinancialsRow = z.infer<typeof assetFinancialsRowSchema>;

/** Paginated response for `GET /financials/asset-financials` (SIL-573). */
export const assetFinancialsResponseSchema = z.object({
  assets: z.array(assetFinancialsRowSchema),
  pageMeta: pageMetaSchema,
  /** Fleet-wide ERP-matched share, e.g. 21/56 — same coverage stat already
   * computed by `erp-coverage.ts` for the Financial Position banner, echoed
   * here so the report's own stat row traces to the same real number rather
   * than a client-side recount over one page of results. */
  erpMatchedCount: z.number().int().nonnegative(),
  erpTotalCount: z.number().int().nonnegative(),
  generatedAt: z.string(),
});
export type AssetFinancialsResponse = z.infer<typeof assetFinancialsResponseSchema>;

/** Query params for `GET /financials/asset-financials` — same cursor
 * pagination shape as `financialsDrillDownQuerySchema`, but no
 * `assetClassId` (this report is deliberately fleet-wide, not per-class). */
export const assetFinancialsQuerySchema = z.object({
  cursor: pageCursorSchema.optional(),
  limit: z.coerce.number().int().positive().max(500).optional(),
});
export type AssetFinancialsQuery = z.infer<typeof assetFinancialsQuerySchema>;

/** One period of a depreciation schedule walk (spec §3.2 "month-by-month to
 * useful_life"). `month` is months-elapsed-since-in-service-at, 0-based. */
export const depreciationSchedulePeriodSchema = z.object({
  month: z.number().int().nonnegative(),
  grossBookValueUsd: z.coerce.number(),
  accumulatedDepreciationUsd: z.coerce.number(),
  netBookValueUsd: z.coerce.number(),
});
export type DepreciationSchedulePeriod = z.infer<typeof depreciationSchedulePeriodSchema>;

/** Per-class depreciation schedule row (`GET /financials/depreciation`, T7).
 * Epic 3 — same group generalization as `bookVsFmvClassRowSchema` above.
 * `schedule` is only ever a non-empty forward walk for `dimension ===
 * 'class'`: a lot/team/site group can span classes on different methods and
 * rates, so there is no single curve to walk — those groups still report
 * `policyTier` (possibly `'mixed'`) and the aggregate totals, just an empty
 * `schedule`. */
export const depreciationScheduleClassRowSchema = z.object({
  assetClassId: z.string().nullable(),
  assetClassName: z.string().nullable(),
  groupId: z.string().nullable(),
  groupLabel: z.string(),
  policyTier: groupPolicyTierSchema,
  method: depreciationMethodSchema.nullable(),
  usefulLifeYears: z.coerce.number().nullable(),
  schedule: z.array(depreciationSchedulePeriodSchema),
  /**
   * SIL-297 (W2) CalculationBasis retrofit — Level-2 basis disclosure for
   * this class's depreciation walk (gross book value source/fallback,
   * resolved policy tier's annual rate, weighted age). Optional — strictly
   * additive per spec Cross-cutting. Attached per class row (not once for
   * the whole response) because its genuinely class-differing input is
   * `policyTier` (spec: "one per class row only where inputs genuinely
   * differ by class"), same cardinality rule the SIL-291 W0 reference
   * retrofit applied to `GET /financials/summary`.
   */
  basis: calculationBasisSchema.optional(),
});
export type DepreciationScheduleClassRow = z.infer<typeof depreciationScheduleClassRowSchema>;

/** Response for `GET /financials/depreciation` (T7). */
export const depreciationScheduleResponseSchema = z.object({
  classes: z.array(depreciationScheduleClassRowSchema),
  /** Epic 3 (lots-first-class) — which dimension `classes` is grouped by. */
  dimension: financialsGroupDimensionSchema,
  generatedAt: z.string(),
});
export type DepreciationScheduleResponse = z.infer<typeof depreciationScheduleResponseSchema>;

/** Per-asset depreciation schedule row for `?assetClassId=&cursor=&limit=`. */
export const depreciationScheduleAssetRowSchema = z.object({
  assetId: uuidSchema,
  assetClassId: z.string(),
  policyTier: policyTierSchema,
  schedule: z.array(depreciationSchedulePeriodSchema),
});
export type DepreciationScheduleAssetRow = z.infer<typeof depreciationScheduleAssetRowSchema>;

/** Paginated per-asset depreciation schedule response (T7). */
export const depreciationScheduleAssetsResponseSchema = z.object({
  assets: z.array(depreciationScheduleAssetRowSchema),
  pageMeta: pageMetaSchema,
});
export type DepreciationScheduleAssetsResponse = z.infer<
  typeof depreciationScheduleAssetsResponseSchema
>;

/** Per-class opportunity-cost row (`GET /financials/opportunity`, T7).
 * Epic 3 — same group generalization as `bookVsFmvClassRowSchema` above. */
export const opportunityCostClassRowSchema = z.object({
  assetClassId: z.string().nullable(),
  assetClassName: z.string().nullable(),
  groupId: z.string().nullable(),
  groupLabel: z.string(),
  assetCount: z.number().int().nonnegative(),
  fmvMedUsd: z.coerce.number().nullable(),
  itadBaselineUsd: z.coerce.number().nullable(),
  netBookValueUsd: z.coerce.number().nullable(),
  /** fmvMedUsd - itadBaselineUsd — the hold-cost narrative (§3.3, §4). */
  opportunityCostUsd: z.coerce.number().nullable(),
  /**
   * SIL-297 (W2) CalculationBasis retrofit — Level-2 basis disclosure for
   * this class's opportunity cost (FMV source, ITAD baseline source +
   * candid `isFallback` when the platform-default ITAD recovery rate
   * substitutes for a missing `itad_rate` row, SIL-280). Optional — strictly
   * additive per spec Cross-cutting. Attached per class row — FMV/ITAD
   * baseline genuinely differ by class (spec cardinality rule).
   */
  basis: calculationBasisSchema.optional(),
});
export type OpportunityCostClassRow = z.infer<typeof opportunityCostClassRowSchema>;

/** Response for `GET /financials/opportunity` (T7). */
export const opportunityCostResponseSchema = z.object({
  classes: z.array(opportunityCostClassRowSchema),
  /** Epic 3 (lots-first-class) — which dimension `classes` is grouped by. */
  dimension: financialsGroupDimensionSchema,
  totalOpportunityCostUsd: z.coerce.number().nullable(),
  generatedAt: z.string(),
});
export type OpportunityCostResponse = z.infer<typeof opportunityCostResponseSchema>;

// ---------------------------------------------------------------------------
// Cursor pagination query params shared by the drill-down GETs
// ---------------------------------------------------------------------------

export const financialsDrillDownQuerySchema = z.object({
  assetClassId: z.string().optional(),
  /** Epic 3 (lots-first-class) — "Group by" dimension for the class-
   * aggregated (no-`assetClassId`) branch. The `assetClassId` per-asset
   * drill-down is unaffected by this param and stays class-scoped
   * regardless — a drill-down under an arbitrary dimension isn't part of
   * this epic's scope. */
  dimension: financialsGroupDimensionSchema.optional(),
  cursor: pageCursorSchema.optional(),
  limit: z.coerce.number().int().positive().max(500).optional(),
});
export type FinancialsDrillDownQuery = z.infer<typeof financialsDrillDownQuerySchema>;

// ---------------------------------------------------------------------------
// 6. Outlook band (asset-financials screen, PR 1 of 2) — `GET
//    /financials/outlook`. Aggregate-then-forecast: the SAME contract as
//    `fmv-outlook.ts`'s `/dashboard` route and `loadAssetFinancialsPage`
//    above (module header, financials-report.ts:88-93) — one SQL pass over
//    the RLS-scoped portfolio grouped by asset class, then ONE kernel
//    (`fmv-forecast.ts`) projection per class. Cost is O(#asset classes),
//    never O(#assets).
//
//    Coverage-gated (spec decision, this PR): below
//    `FMV_OUTLOOK_COVERAGE_FLOOR_PCT` of portfolio VALUE priced (book value
//    is the value proxy — every owned asset resolves one, priced or not;
//    FMV only exists for priced assets), every forecast-derived field is
//    null and `coverage` alone is returned — never a forecast computed over
//    a minority of the portfolio and presented as the whole (spec "Coverage
//    gate").
// ---------------------------------------------------------------------------

/** Below this share of portfolio value carrying an FMV snapshot, the
 * Outlook band's forecast fields are null (coverage gate). Shared by the
 * API (gate decision) and the UI (empty-state copy), so the two numbers
 * never drift. */
export const FMV_OUTLOOK_COVERAGE_FLOOR_PCT = 0.6;

/** Below this share of `totalFmvMedUsd` held by the same-store trailing-trend
 * cohort, the trend is nulled out rather than rendered — a same-store slice
 * that is too small to be representative of the portfolio must never
 * headline beside the portfolio-wide FMV total (e.g. a 2-asset, $1.2k
 * cohort next to a $303k total). 10% is deliberately looser than
 * `FMV_OUTLOOK_COVERAGE_FLOOR_PCT` (which gates the forecast itself): the
 * trend is a supporting indicator next to the total, not a headline number
 * in its own right, so it tolerates a thinner slice before going quiet. */
export const FMV_OUTLOOK_TREND_COHORT_FLOOR_PCT = 0.1;

/** How far before `windowStart` a same-store trend's START-of-window anchor
 * is allowed to be. `loadSameStoreTrailingTrend`'s start query picks each
 * asset's MOST RECENT snapshot at or before `windowStart` via `DISTINCT ON`
 * — with no staleness bound, "most recent" can mean "the only snapshot that
 * asset has ever had," arbitrarily far in the past (a 2025-01-01 seed
 * placeholder compared against a 2026-07 first-real-price read as +212%
 * "trailing 90d" in production — the bug this constant fixes, distinct from
 * and additional to the #1117 $0-snapshot fix). 30 days matches this file's
 * monthly repricing cadence (see `loadFinancialsOutlook`'s use of monthly
 * buckets elsewhere in `financials-report.ts`): a real monthly reprice
 * always lands an anchor within 30 days of any `windowStart`, so 30 days
 * costs no genuinely-current same-store asset its anchor while still
 * rejecting anything that is actually stale (a skipped cycle or, as in
 * production, a placeholder that predates real pricing entirely). Assets
 * whose only pre-`windowStart` snapshot falls outside this bound are
 * excluded from the same-store cohort — same treatment as an asset with no
 * start snapshot at all. */
export const FMV_OUTLOOK_TREND_START_ANCHOR_LOOKBACK_DAYS = 30;

/**
 * Per-asset delta-sum definition (reworked from the earlier aggregate-ratio
 * definition — spec "Outlook trend: per-asset delta-sum"): the trend is the
 * SUM of each contributing asset's own (end - start) FMV delta over the
 * window, never a ratio of portfolio totals. An asset contributes ONLY if it
 * has a qualifying FMV observation near BOTH ends of the window — no prior
 * value (never onboarded, or no FMV yet) contributes null/0, and critically
 * a prior value that is real but STALE (older than
 * `FMV_OUTLOOK_TREND_START_ANCHOR_LOOKBACK_DAYS` before windowStart) ALSO
 * contributes null/0, never a fabricated delta from a stale anchor — see
 * `loadSameStoreTrailingTrend`'s header for the production shape that made
 * this distinction necessary (a 19-month-stale seed placeholder compared
 * against a brand-new real price read as a manufactured +212%/"+$7,336"
 * swing under a naive reading of "no prior value → 0"). `deltaUsd` is sound
 * to display as-is because it reconciles with the headline `totalFmvMedUsd`
 * by construction (unlike the `absChangeUsd` #1117 dropped for exactly the
 * opposite reason — a figure computed against a population the headline
 * total didn't share).
 */
export const financialsOutlookTrailingTrendSchema = z.object({
  /** PRIMARY figure. Sum of each contributing asset's own (end - start) FMV
   * delta over the window — signed (a declining cohort sums negative), never
   * an absolute value. */
  deltaUsd: z.coerce.number(),
  /** SECONDARY, optional. `deltaUsd` / (summed START value of the
   * CONTRIBUTING assets only) — never computed over the portfolio total.
   * Null when the contributing cohort's summed start value is zero (can't
   * divide by zero, and a zero base makes a ratio meaningless anyway). */
  pctChange: z.coerce.number().nullable(),
  /** Number of assets with a qualifying observation at BOTH ends of the
   * window (start anchor within `FMV_OUTLOOK_TREND_START_ANCHOR_LOOKBACK_DAYS`
   * of windowStart, both endpoints `fmv_med_usd > 0`). Surfaced so callers
   * can judge materiality without re-deriving it — the API already nulls out
   * the whole trend when this cohort's value is too small a share of
   * `totalFmvMedUsd` (`FMV_OUTLOOK_TREND_COHORT_FLOOR_PCT`), but the count
   * travels with the DTO regardless for transparency/debugging. */
  contributingAssetCount: z.coerce.number().int(),
  /** Contributing cohort's CURRENT (window-end) value as a share of
   * `totalFmvMedUsd` — the materiality-gate input, exposed alongside
   * `contributingAssetCount` for the same transparency reason. */
  contributingSharePct: z.coerce.number(),
  /** 'YYYY-MM-DD', 90 calendar days before windowEndDay. */
  windowStartDay: z.string(),
  windowEndDay: z.string(),
});
export type FinancialsOutlookTrailingTrend = z.infer<typeof financialsOutlookTrailingTrendSchema>;

export const financialsOutlookProjectedSchema = z.object({
  /** Portfolio value at the ~90-day mark on the aggregated forecast curve.
   * Classes with no forecastable slope (no market rate AND no age-curve
   * bracket for their age) hold their current value flat rather than being
   * dropped from the total — never a forecast for less than the full
   * priced portfolio presented as the whole. */
  valueUsd: z.coerce.number(),
  deltaUsd: z.coerce.number(),
  pctChange: z.coerce.number(),
});
export type FinancialsOutlookProjected = z.infer<typeof financialsOutlookProjectedSchema>;

export const financialsOutlookRecoveryPointSchema = z.object({
  /** 'YYYY-MM'. */
  month: z.string(),
  /** Aggregated projected net recovery (FMV minus friction) — each
   * forecastable class's current net-recovery total is scaled by that
   * SAME class's FMV forecast ratio at this point (no independent net-
   * recovery model — see the route's doc comment). */
  netRecoveryUsd: z.coerce.number(),
  /** Aggregated book-depreciation reference line. Null for every point
   * when NOT ONE asset class in the portfolio has a resolvable book
   * anchor (never a fabricated $0 book line). */
  bookValueUsd: z.coerce.number().nullable(),
});
export type FinancialsOutlookRecoveryPoint = z.infer<typeof financialsOutlookRecoveryPointSchema>;

export const financialsOutlookCoverageSchema = z.object({
  /** Share of portfolio (book) value that is currently FMV-priced — the
   * coverage-gate input. */
  pricedValuePct: z.coerce.number(),
  pricedAssetCount: z.number().int().nonnegative(),
  totalAssetCount: z.number().int().nonnegative(),
});
export type FinancialsOutlookCoverage = z.infer<typeof financialsOutlookCoverageSchema>;

export const financialsOutlookResponseSchema = z.object({
  /** Current portfolio FMV total — same SUM-across-classes-by-construction
   * idiom `dashboard.ts`'s `/summary` route uses for `totalFmvMedUsd`. */
  totalFmvMedUsd: z.coerce.number(),
  /** D8 (probe asset grouping): count of top-level assets whose contribution
   * to `totalFmvMedUsd` is estimated from their priced components rather
   * than their own System FMV. 0 when the total is pure System FMV; the UI
   * labels the tile "est. from components" when it is above 0. */
  componentEstimatedAssetCount: z.number().int().nonnegative(),
  /** Null when fewer than one same-store asset spans the full 90-day
   * window (e.g. a brand-new portfolio). */
  trailingTrend: financialsOutlookTrailingTrendSchema.nullable(),
  /** Null below the coverage floor. */
  projected90d: financialsOutlookProjectedSchema.nullable(),
  /** Earliest per-class Refresh Cliff across the portfolio — the soonest
   * bracket-boundary drop bites first. Null below the coverage floor or
   * when no class has one inside the 12-month window. */
  refreshCliff: refreshCliffSchema.nullable(),
  /** Null below the coverage floor. */
  recoverySeries: z.array(financialsOutlookRecoveryPointSchema).nullable(),
  coverage: financialsOutlookCoverageSchema,
  asOf: z.object({
    /** Most recent `fmv_pricing_snapshot.priced_at` across the org's
     * current per-class latest snapshots. Null when nothing is priced. */
    latestSnapshotAt: z.string().nullable(),
    /** Least recent — the widest staleness in the current portfolio. */
    oldestSnapshotAt: z.string().nullable(),
  }),
  generatedAt: z.string(),
});
export type FinancialsOutlookResponse = z.infer<typeof financialsOutlookResponseSchema>;

// ---------------------------------------------------------------------------
// 7. Market demand (SIL-573 PR 2 of 2, Outlook band's last tile) — `GET
//    /financials/market-demand`. "Demand for what you hold," NOT "offers
//    you can accept" — a deliberately broader statistic than
//    `opportunities.ts`'s feed (see `apps/api/src/lib/market-demand.ts`'s
//    module header for the full match-predicate rationale: SKU grain only,
//    no price threshold, all lifecycles, shown to opted-out orgs too).
//
//    Privacy: counts/quantities only, k-anonymity floor
//    (`COMPS_K_ANONYMITY_FLOOR`) applied the same way market-trends.ts
//    applies it to comps figures — `topDemand` only ever names ONE bucket
//    (SKU, falling back to asset-class) whose own distinct-buyer count
//    clears the floor. The top-level `buyerCount` is NOT floor-gated (see
//    `apps/api/src/lib/market-demand.ts`'s module header): it aggregates
//    across every SKU the seller holds, so it doesn't carry the
//    thin-single-SKU-market signal the floor exists to suppress, and
//    zeroing it below the floor would misrepresent real demand as none.
// ---------------------------------------------------------------------------

export const marketDemandTopEntrySchema = z.object({
  grain: z.enum(['sku', 'assetClass']),
  label: z.string(),
  buyerCount: z.number().int().nonnegative(),
  quantityDesired: z.number().int().nonnegative(),
});
export type MarketDemandTopEntry = z.infer<typeof marketDemandTopEntrySchema>;

export const marketDemandResponseSchema = z.object({
  /** Distinct buyer_customer_org_id across all matching open direct_offer
   * rows — the TRUE count, not floor-gated. Aggregates across every SKU
   * the org holds, so it doesn't carry the thin-single-SKU-market signal
   * COMPS_K_ANONYMITY_FLOOR exists to suppress (see market-demand.ts). */
  buyerCount: z.number().int().nonnegative(),
  /** Summed quantity_desired across all matching open offers. Not
   * floor-gated — a quantity total, unlike a buyer count, does not by
   * itself narrow to an identifiable buyer. */
  totalQuantityDesired: z.number().int().nonnegative(),
  /** How many of the org's owned SKUs have any open demand at all. */
  matchedSkuCount: z.number().int().nonnegative(),
  /** The single highest-demand entry that clears the k-anonymity floor —
   * SKU grain preferred, falling back to asset-class grain, null when
   * nothing clears the floor at either grain. This is the ONLY place a
   * specific SKU or class is named, and it is floor-gated server-side. */
  topDemand: marketDemandTopEntrySchema.nullable(),
  /** The org's own accept_unsolicited_offers value — gates solicitation
   * only, not this read (spec: "shown to opted-out orgs too"). */
  offersEnabled: z.boolean(),
  generatedAt: z.string(),
});
export type MarketDemandResponse = z.infer<typeof marketDemandResponseSchema>;
